/**
 * One engine, three backends.
 *
 * Everything the store *decides* lives here; everything a backend does is the
 * two calls in `StorePersistence`. That split is the whole reason a
 * server-backed store can replace the local one without a caller noticing: an
 * IndexedDB store and a memory store are this class twice, with a different
 * patch sink underneath.
 */

import {
  ROLE_KIND, ROLES, DERIVATIVE_OF, enumPairKey, isVisualKind, mutationAcceptsRole,
  validateSpec,
  type Operator, type RelationMutation, type Role, type TBinding, type TLayoutSpec,
  type TNode, type TQuery,
} from '@morph/spec';
import type { RoleValues } from '@morph/runtime';
// The renderer's formatter, imported rather than reimplemented. Its own comment
// says it is exported so a second copy cannot drift — and a group label that
// disagrees with the renderer's puts a group's total under a column with a
// different name, which is worse than showing no total at all.
import { formatValue, RELATION_TARGET_KEY } from '@morph/runtime';
import {
  CLEAR, isRefusal, refuse,
  type Actor, type AnyGrant, type BindingAuthority, type Clear, type EditorAuthority,
  type Grant, type Instruction, type Refusal, type RelationSlot,
  type RelationTargetHandle, type WriteBinding, type WritePolicy,
} from './authority.js';
import { EDITOR, GRANT, RELATION_TARGET, WRITABLE } from './brand.js';
import { Counters, type Contribution } from './digest.js';
import {
  asRevision, newEdgeId, newJournalId, newObjectId, newPropertyKeyId,
  type AnchorId, type EdgeId, type JournalId, type ManifestRef,
  type ObjectId, type PropertyKeyId, type Revision,
} from './ids.js';
import type {
  ManifestEntry, PropertyKeyRecord, PropertySlot, StoredObject,
} from './model.js';
import { MUTATION_ROLE_KINDS } from './mutations.js';
import type {
  InverseStep, PersistedEdge, PersistedJournal, PersistedKey, PersistedObject,
  CommitOutcome, PersistPatch, RestorePatch, RestoreScope, StorePersistence,
} from './persistence.js';
import type {
  AggregateResult, AggregateSpec,
  ReadOptions, ReadSnapshot, StoreEvent, Traversal, TraversalEdge, TraversalResult, Unsubscribe,
} from './read.js';
import { foldKey } from './read.js';
import { scopeKey, stableHash, type Page, type Scope, type Slice } from './scope.js';
import type { DeriveOptions, ShapeDigest, ShapeRole, StoredShape } from './shape.js';
import type { ObjectStore, StoreOptions } from './store.js';
import { isPopulated, kindOf, rawOf, valueFitsKind, type Value } from './value.js';
import type { BoundSession, CommitResult, EditSession, JournalEntry } from './write.js';

interface ObjectRecord {
  readonly id: ObjectId;
  readonly revision: Revision;
  readonly typeHint?: string;
  readonly slots: ReadonlyMap<PropertyKeyId, PropertySlot>;
  readonly deletedAt?: string;
}

interface EdgeRecord {
  readonly id: EdgeId;
  readonly from: ObjectId;
  /**
   * The PRIVATE key this relation is recorded under — the same kind of key a
   * ref-valued slot uses, which is the point: decision 0016 refuses a public
   * role as storage identity, and an edge was the one place in this store that
   * still used one.
   *
   * Optional because data written before this exists carries only `via`. Such
   * an edge still READS — losing a seeded relation to a schema change would be
   * a far worse bug than the one being fixed — and cannot be mutated, because
   * there is nothing for a grant to name.
   */
  readonly key?: PropertyKeyId;
  /** The public role, derived from the key's `roleHint` at link time. Kept on
   *  the record so a read never has to reach the key registry, and so a legacy
   *  edge with no key still projects. */
  readonly via: Role;
  readonly to: ObjectId;
  readonly anchor?: AnchorId;
  readonly at: string;
}

/**
 * A fold's running totals — the state a write ADJUSTS instead of discarding.
 *
 * Parallel arrays rather than objects, indexed by the plan's role order:
 * a grouped fold holds one of these per group, and an accumulator object per
 * group per role is the allocation problem task 0056 removed one level up.
 */
interface FoldBucket {
  n: number;
  seen: number[];
  carried: number[];
  sum: number[];
  /** Maintained on the way in and WRONG on the way out — see `FoldPlan.updatable`. */
  min: number[];
  max: number[];
  first: number[];
  last: number[];
}

interface FoldPlan {
  readonly specs: readonly AggregateSpec[];
  readonly scope: Scope;
  readonly mi: ManifestIndex;
  readonly groupBy?: Role;
  readonly roles: readonly Role[];
  readonly keysOf: readonly (readonly PropertyKeyId[])[];
  readonly slotFor: ReadonlyMap<Role, number>;
  readonly numeric: readonly boolean[];
  readonly updatable: boolean;
}

interface FoldAcc {
  readonly plan: FoldPlan;
  readonly total: FoldBucket;
  readonly groups?: Map<string, FoldBucket>;
}

interface ManifestIndex {
  readonly ref: ManifestRef;
  readonly entries: readonly ManifestEntry[];
  readonly keysByRole: ReadonlyMap<Role, readonly PropertyKeyId[]>;
  readonly roleByKey: ReadonlyMap<PropertyKeyId, Role>;
}

/**
 * The manifests one projection may read through: the scope's own, plus the
 * ones it declared in `Scope.across`.
 *
 * Two fields rather than one list, because they are not interchangeable. `mi`
 * decides which objects are IN the lens and which roles resolve on them.
 * `across` decides nothing about membership — it is consulted only when a
 * reference already points out of the pool, and only to read the thing pointed
 * at. Collapsing them into one list would make every declared document's
 * objects readable by name, which is the boundary this is built to keep.
 */
interface Lens {
  readonly mi: ManifestIndex;
  readonly across: readonly ManifestIndex[];
  /** Every manifest a reference may resolve through, at any depth: the scope's
   *  own plus the ones it declared. Computed once and held CONSTANT down the
   *  recursion, which is the property that keeps `across` a declaration —
   *  a declared document cannot introduce a further one, so the reachable set
   *  at depth four is the set the caller wrote down. */
  readonly reachable: readonly ManifestIndex[];
}

interface JournalRecord {
  readonly id: JournalId;
  readonly at: string;
  readonly via: 'binding' | 'editor';
  readonly specHash?: string;
  readonly touched: readonly ObjectId[];
  readonly steps: readonly InverseStep[] | null;
  readonly restore: RestorePatch;
  /**
   * The manifest this commit PUBLISHED, when it published one.
   *
   * `restore.manifest` is the one it replaced; this is the other end of the
   * same swap, and undo needs it to be symmetric — see the note where `undo`
   * builds its own journal.
   *
   * Optional, and absent on every record written before it existed. A redo of
   * one of those behaves exactly as it did, which is the point: this adds a
   * fact rather than changing the meaning of a missing one.
   */
  readonly publishedManifest?: string;
}

const DEFAULT_MAX_SAMPLE = 500;
/**
 * How many distinct values an enum sketch will hold before it saturates.
 *
 * Exported because a chooser's honesty depends on the relation between this
 * and generation's `groupableCardinalityMax`: a role is only offered a chooser
 * below that threshold, and the options come from this sketch, so a truncated
 * dropdown is unreachable only while the threshold stays under the cap. Two
 * numbers in two packages with nothing asserting the relation is exactly how
 * that stops being true quietly — `apps/demo/test/enum-cap.test.ts` ties them.
 */
export const DEFAULT_ENUM_CAP = 200;

const opt = <K extends string, V>(k: K, v: V | undefined) =>
  (v === undefined ? {} : { [k]: v }) as { [P in K]?: V };

/**
 * The brand check the write doors rest on.
 *
 * `authority.ts` argues that an undeclared mutation has **no expressible form**,
 * and until these two functions existed that argument was type-level only:
 * nothing ever read the symbol, so a caller never needed to name it. A plain
 * object literal carrying a `writable` map was a fully working write authority —
 * any mutation, on any role, with no spec involved — and `{ actor }` was a
 * fully working editor.
 *
 * Not exported: naming a brand is exactly what an authority must not be able to
 * do. Decision 0010's open capability-module registry puts third-party code in
 * this realm on purpose, and TypeScript is not there when it runs.
 */
const isBindingAuthority = (a: unknown): a is BindingAuthority =>
  typeof a === 'object' && a !== null && (a as { [WRITABLE]?: unknown })[WRITABLE] === true;

const isEditorAuthority = (a: unknown): a is EditorAuthority =>
  typeof a === 'object' && a !== null && (a as { [EDITOR]?: unknown })[EDITOR] === true;

const FORGED = 'this authority was not minted by the store, so nothing has checked what it '
  + 'may do';

export class StoreEngine implements ObjectStore {
  readonly backend: 'memory' | 'indexeddb' | 'remote';

  private readonly objects = new Map<ObjectId, ObjectRecord>();
  private readonly keys = new Map<PropertyKeyId, PropertyKeyRecord>();
  private readonly edges = new Map<EdgeId, EdgeRecord>();
  private readonly outEdges = new Map<ObjectId, Set<EdgeId>>();
  private readonly inEdges = new Map<ObjectId, Set<EdgeId>>();
  private readonly manifests = new Map<ManifestRef, ManifestIndex>();
  /**
   * Minted relation targets, by token.
   *
   * Decision 0016 wants a handle that *"may not be constructed from typed
   * text"* and does not expose a raw id, so the token is a hash of the target
   * under a per-engine secret and the mapping lives here. Guessing one means
   * guessing the secret.
   *
   * Minting is IDEMPOTENT over `(object, anchor)`, which is also what
   * "occurrence-specific" means once you look at what distinguishes two
   * occurrences: the anchor, and the private relation key. Two links to one
   * object through one anchor in one relation set are one member — that is the
   * uniqueness rule — and the key is carried by the grant, not by the handle.
   * So the map is bounded by the targets actually rendered rather than by how
   * many times they were rendered.
   */
  private readonly targets = new Map<string, { object: ObjectId; anchor?: AnchorId }>();
  private readonly targetSecret = newObjectId();
  private readonly journal = new Map<JournalId, JournalRecord>();
  private readonly digests = new Map<string, Counters>();
  /** Keyed by `scopeKey`, and holding the scope itself: an event has to be
   *  filtered against what the subscriber is actually looking at, and a key
   *  alone cannot answer that. */
  private readonly listeners = new Map<
    string, { scope: Scope; listeners: Set<(e: StoreEvent) => void> }
  >();

  /** Scopes asked for before their manifest existed. A caller naming the scopes
   *  it cares about at construction is the normal case, and at construction the
   *  store is empty — so registration waits for the manifest rather than
   *  throwing at the one moment nobody can have satisfied it yet. */
  private readonly pendingScopes: Scope[] = [];

  private readonly persistence: StorePersistence;
  private readonly policy: WritePolicy | undefined;
  private readonly now: () => string;
  private digestClock = 0;
  /**
   * The store-wide commit revision this engine believes the backend is at.
   *
   * Decision 0027's basis. It is only ever a BELIEF — a second tab moves the
   * real one without telling anybody — which is precisely why the comparison
   * happens in the backend and this value is what gets compared, not trusted.
   */
  private storeRevision = 0;
  /** Decision 0029: announced after the backend accepts, never before. */
  private readonly onCommitted: ((revision: number) => void) | undefined;
  private closed = false;

  /**
   * The id-sorted membership base, or undefined after any object mutation.
   *
   * The two writers below are the only ways to mutate `this.objects`. Direct
   * invalidation keeps a stale snapshot from retaining every replaced record
   * until a later read and needs no generation counter.
   */
  private sortedBase: ObjectRecord[] | undefined;

  /**
   * Membership per scope — the filtered, sorted list `members` hands back.
   *
   * `sortedBase` removed the repeated id SORT; this removes the repeated
   * FILTER. A render makes two membership reads with no write between them
   * (`query` and `projection`, plus `count` where a total is drawn), and each
   * one walked all 100,000 records through `inScope` and allocated a
   * 100,000-element array to hold the survivors. Measured after the fold memo:
   * the render path was 18.2ms and `count` alone was 8.65ms of it.
   *
   * Cleared wholesale by any object change rather than adjusted. A fold is a
   * running total and can have one object taken back out of it; a membership
   * list is ORDERED, and inserting into the right place under an arbitrary
   * `query.sort` is a different problem — one this does not need to solve,
   * because the cost being removed is the repeat WITHIN a render, not the first
   * read after a write.
   *
   * **That last clause is no longer true, and the number is now known.**
   * 2026-08-29, task `0139`: `morph.render.run` measured the SAME `run()` in a
   * browser at 100,000 objects, once after a scheme change and once after a
   * write.
   *
   *     warm (no write)      9.9ms · 10.6ms
   *     cold (after a write) 25.1ms · 25.4ms
   *
   * The demo's own compile-and-render timer is ~2ms in both, so the ~15ms is
   * this memo being empty — `deriveShape`, `query`, `projection` and
   * `aggregate` each walking 100,000 records again. A rerender has 16.7ms, so
   * the first read after a write is what makes the frame miss. It is the whole
   * of what `Agents/Tasks/0039` has left, and the bar's scale row fails on it.
   *
   * The objection above stands and is why this is a NOTE rather than a change:
   * an ordered insert under an arbitrary `query.sort` has to reproduce
   * `compareRaw`'s tie-breaks exactly, and getting it subtly wrong mis-orders
   * the store's answer for every later caller, silently. `0039` says that half
   * "wants its own review", and it should get one — this comment is here so
   * whoever does it starts from a measured cost rather than from the sentence
   * above, which was written before anybody had one.
   *
   * The array is handed to callers directly, and every caller treats it as
   * read-only: `paginate` slices, `aggregate` and `scan` iterate, `count` takes
   * a length. A caller that sorted it would be re-ordering the store's answer
   * for everyone after it, which is why `members` returns `readonly`.
   */
  private membership = new Map<string, readonly ObjectRecord[]>();

  /** Same bound and same reasoning as the fold cache: one entry per lens on
   *  screen in practice, and a purely reading workload across many lenses
   *  should not grow it for ever. */
  private static readonly MEMBERSHIP_CACHE_MAX = 64;

  /**
   * Folds already computed, cleared by the same two writers below.
   *
   * A rerender makes ONE `aggregate` call, and a rerender that changed no data
   * makes the same one — a theme change, a resize, a lens switch. At 100k that
   * call was most of the frame, so serving it from here is most of the frame
   * back. Task 0056 measured the fold itself down from 13.7ms to 6.5ms and left
   * this; task 0057 fixed the instrument that would have been fooled by it.
   *
   * **The key is `scopeKey` plus the specs plus `groupBy`, and every part is
   * load-bearing.** `scopeKey` already folds in the manifest, filter, sort,
   * viewState, seed, typeHint and `across` — it exists because two lenses
   * differing only in what they may read across are two lenses, and the same
   * sentence is why a fold cannot be shared between them. The specs are in the
   * key because a fold answers exactly the questions it was asked; the answer
   * to `sum` is not the answer to `sum` and `count`.
   *
   * **It survives a write now**, which the first version deliberately did not:
   * *"any mutation empties the whole map. That is the cheap correct thing and
   * it is also the honest one — the case a person feels is the frame after an
   * edit, and this makes that frame no faster."* Right about the cost, and that
   * cost was measured: at 100k a grouped fold was 106ms on the frame after an
   * edit, most of the 133ms the acceptance row is judged on.
   *
   * So the map holds ACCUMULATORS rather than answers, and `updateFolds`
   * adjusts them by the objects a commit touched. `e2e/frame-time.mjs` still
   * measures both interactions and still takes the worse, so this cannot
   * flatter its own row.
   */
  private folds = new Map<string, FoldAcc>();

  /** A bound, because the map is only emptied by writes and a purely reading
   *  workload across many distinct lenses would otherwise grow it for ever.
   *  Cleared wholesale rather than evicted one at a time: at the sizes a render
   *  actually produces — one entry per lens on screen — this never fires, and a
   *  policy that never runs should be the simplest one that is correct. */
  private static readonly FOLD_CACHE_MAX = 64;

  /**
   * The only writer. Invalidation stays colocated with mutation.
   *
   * A REPLACEMENT does not invalidate the sorted base — it patches it.
   *
   * The base is sorted by ID, and a value write never changes an id: the
   * membership and its order are exactly what they were. What did change is the
   * RECORD, and the array holds records — which is why this used to throw the
   * whole thing away, and the comment above it says so: *"direct invalidation
   * keeps a stale snapshot from retaining every replaced record."* True, and
   * the cheaper answer to it is to put the new record where the old one was.
   *
   * Binary search rather than `indexOf`: the array is sorted by the very thing
   * being looked up, so this is O(log n) against a linear scan, and against the
   * O(n log n) re-sort it replaces. Measured at 100k: `count` cold after a
   * write was 18.2ms against 7.6ms warm, and the difference was one
   * `localeCompare` sort of every object in the store.
   *
   * A CREATE still invalidates, because membership really did change and the
   * new id belongs somewhere this cannot know without looking.
   */
  private putObject(id: ObjectId, rec: ObjectRecord): void {
    const replacing = this.objects.has(id);
    this.objects.set(id, rec);
    if (this.sortedBase) {
      const at = replacing ? StoreEngine.indexInIdOrder(this.sortedBase, id) : -1;
      // `at < 0` covers both a create and the case that should be impossible —
      // a replacement the base does not hold. Falling back to the re-sort there
      // rather than trusting the invariant: a wrong membership list is a wrong
      // page, and this is the one place that could produce one silently.
      if (at < 0) this.sortedBase = undefined;
      else this.sortedBase[at] = rec;
    }
    // The folds are NOT cleared here. `applyCommit` adjusts them by the objects
    // a commit touched, and it has to do that while `this.objects` still holds
    // the BEFORE records — which is one line above this one. Clearing here as
    // well would throw away the work that call just did.
    //
    // `hydrate` is the other caller and reaches this before any fold exists.
  }

  /** Where an id sits in an id-sorted array, or -1. The comparator has to be
   *  the one that built the array, or the search walks the wrong half. */
  private static indexInIdOrder(base: readonly ObjectRecord[], id: ObjectId): number {
    let lo = 0;
    let hi = base.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const cmp = base[mid]!.id.localeCompare(id);
      if (cmp === 0) return mid;
      if (cmp < 0) lo = mid + 1; else hi = mid - 1;
    }
    return -1;
  }

  /** The only remover. `undo` of a create is the one path that reaches it. */
  private forgetObject(id: ObjectId): void {
    this.objects.delete(id);
    this.sortedBase = undefined;
    // See `putObject`: folds are adjusted before the object mutations run.
  }


  constructor(
    backend: 'memory' | 'indexeddb' | 'remote',
    persistence: StorePersistence,
    options: StoreOptions = {},
  ) {
    this.backend = backend;
    this.persistence = persistence;
    this.policy = options.policy;
    this.onCommitted = options.onCommitted;
    this.now = options.now ?? (() => new Date().toISOString());
    this.pendingScopes.push(...(options.registerScopes ?? []));
  }

  /* ────────────────────────────────────────────────────────────── snapshot */

  /**
   * Read from ONE coherent point, however many awaits it takes.
   *
   * VALIDATED RETRY, not a lock and not a copy, and the choice is forced from
   * three directions:
   *
   *   a copy      the scale route holds 100,000 objects; cloning the maps per
   *               read is a different product
   *   a lock      a reader that can stall a commit stalls typing, and a commit
   *               that can stall a reader deadlocks against the save queue
   *   a reload    decision 0027 refuses it by name — it "would make every
   *               keystroke a full database load"
   *
   * So: note the revision, run the whole read, and check the revision again.
   * `storeRevision` advances on every accepted commit, so if it did not move,
   * nothing landed while the reader was mid-traversal and what it saw was one
   * state. If it did move, the read is discarded and repeated — the caller
   * never sees the torn result, which is the property task 0106 is about.
   *
   * ON THE UNCONTENDED PATH THIS IS ONE INTEGER COMPARISON. That is the whole
   * reason it can sit under a keystroke.
   *
   * The revision handed to `fn` is the VALIDATED one, so a plan built inside
   * here may name it as its commit basis and the two describe the same state.
   * That is decision 0027's requirement and @Codex's ruling that the read side
   * needs no token of its own.
   *
   * `attempts` is a backstop, not a policy. A store under writes heavy enough
   * to lose five reads in a row has a contention problem this cannot paper
   * over, and failing loudly beats returning a document nobody wrote.
   *
   * **DO NOT COMMIT INSIDE `fn`.** The retry is what makes this coherent and it
   * is also what makes a write in here unsafe: a commit that lands and is then
   * followed by a foreign commit would be REPLAYED on the retry, applying it
   * twice. There is no cheap way to tell the caller's own commit from anybody
   * else's — both move `storeRevision` — so this is a rule rather than a check,
   * and it is why the method is called `read`.
   *
   * The safe composition is to plan inside and write outside:
   *
   * ```ts
   * const { plan, revision } = await store.read(async ({ reader, revision }) => ({
   *   plan: await somethingThatOnlyReads(reader), revision,
   * }));
   * const result = await store.edit(editor, revision, (session) => apply(session, plan));
   * // refused as `stale` -> take a NEW read and re-plan; never replay this one
   * ```
   *
   * Decision 0027 says the same thing about the refusal: *"callers must obtain
   * a new snapshot and rebuild rather than replay the old plan"* — replaying is
   * unsafe because an `unlink` plus `link` plan can change meaning once its
   * original edge has moved.
   */
  async read<T>(fn: (snapshot: ReadSnapshot) => Promise<T>, attempts = 5): Promise<T> {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const revision = this.storeRevision;
      const value = await fn({ reader: this, revision });
      if (this.storeRevision === revision) return value;
    }
    throw new Error(
      `a coherent read could not be taken in ${attempts} attempts: the store `
      + 'moved under every one of them',
    );
  }

  /**
   * The snapshot on its own, for a caller that cannot be wrapped.
   *
   * Deliberately thinner than it looks: `reader` is this engine, so the answers
   * are only stable while nothing commits. Anything spanning more than one
   * await must use `read` instead, which is why that one is the primitive and
   * this one exists for a single-await caller and for tests.
   */
  async snapshot(): Promise<ReadSnapshot> {
    return { reader: this, revision: this.storeRevision };
  }

  /* ─────────────────────────────────────────────────────────────── hydrate */

  /**
   * `replace` is the difference between catching up and starting over.
   *
   * Loading over the top of live maps KEEPS what the winner deleted: hydration
   * only overwrites rows the snapshot contains, so an object, key or edge the
   * other engine removed stays visible here. That is fine on a fresh engine,
   * which is the only caller that existed when this was written, and wrong for
   * every refresh that follows a refused write — @Codex, P1 on #164, against
   * the manifest retry below.
   *
   * The load happens BEFORE anything is cleared, and the clear-and-apply that
   * follows is synchronous. A load that throws therefore leaves the engine
   * exactly as it was, and no other task can observe the gap — the earlier
   * order cleared first and could strand this engine permanently empty on a
   * backend failure, or hand a concurrent reader an empty store mid-await.
   */
  async hydrate(options: StoreOptions = {}, replace = false): Promise<void> {
    const snap = await this.persistence.load();
    if (replace) {
      this.objects.clear();
      this.keys.clear();
      this.edges.clear();
      this.manifests.clear();
      this.journal.clear();
      this.outEdges.clear();
      this.inEdges.clear();
    }
    this.storeRevision = snap.revision ?? 0;
    /**
     * The three caches, dropped before anything is loaded over the top.
     *
     * Not defensive tidying. `putObject` deliberately does NOT clear folds or
     * membership — `applyCommit` adjusts the folds and clears the membership
     * itself, before the object writes, because it is the only place that still
     * holds the BEFORE records. `hydrate` calls `putObject` directly and is not
     * `applyCommit`, so it is the one writer that bypasses that arrangement.
     *
     * In practice it runs once on a fresh engine and there is nothing to drop.
     * That is an invariant nobody stated and nothing enforced, and the failure
     * it permits is the worst kind available here: a membership list from
     * before the load is a WRONG PAGE, and a fold from before it is a wrong
     * number under a column heading.
     *
     * Found by checking a claim rather than by a test failing — the claim being
     * that every writer goes through `applyCommit`. This one does not.
     */
    this.folds.clear();
    this.membership.clear();
    this.sortedBase = undefined;
    for (const k of snap.keys) this.keys.set(k.id as PropertyKeyId, fromPersistedKey(k));
    // Hydration is a write like any other, and arrives in whatever order the
    // persistence layer produced — which is not this store's business to assume.
    for (const o of snap.objects) this.putObject(o.id as ObjectId, fromPersistedObject(o));
    for (const e of snap.edges) this.indexEdge(fromPersistedEdge(e));
    for (const m of snap.manifests) {
      this.manifests.set(m.ref as ManifestRef, indexManifest(
        m.ref as ManifestRef,
        m.entries.map((e) => ({ role: e.role, key: e.key as PropertyKeyId })),
      ));
    }
    for (const j of snap.journal) {
      this.journal.set(j.id as JournalId, {
        id: j.id as JournalId, at: j.at, via: j.via,
        ...opt('specHash', j.specHash),
        touched: j.touched as readonly ObjectId[],
        steps: j.steps, restore: j.restore,
        ...opt('publishedManifest', j.publishedManifest),
      });
    }
    this.pendingScopes.push(...(options.registerScopes ?? []));
    this.ensureRegistered();
  }

  private ensureRegistered(): void {
    for (let i = this.pendingScopes.length - 1; i >= 0; i -= 1) {
      const scope = this.pendingScopes[i]!;
      if (!this.manifests.has(scope.manifest)) continue;
      this.pendingScopes.splice(i, 1);
      this.register(scope);
    }
  }

  /** A registered scope keeps live counters, so `deriveShape` folds over keys
   *  rather than objects. Registering after the fact is a full scan once. */
  register(scope: Scope, enumCap = DEFAULT_ENUM_CAP): Counters {
    const key = scopeKey(scope);
    const existing = this.digests.get(key);
    if (existing) return existing;
    const counters = this.scan(scope, enumCap);
    this.digests.set(key, counters);
    return counters;
  }

  /* ───────────────────────────────────────────────────────────────── reads */

  async get(id: ObjectId, opts: ReadOptions): Promise<StoredObject | undefined> {
    const rec = this.objects.get(id);
    if (!rec) return undefined;
    if (rec.deletedAt && !opts.includeDeleted) return undefined;
    return this.materialize(rec, this.manifestOf(opts.manifest));
  }

  async getMany(ids: readonly ObjectId[], opts: ReadOptions): Promise<readonly StoredObject[]> {
    const mi = this.manifestOf(opts.manifest);
    const out: StoredObject[] = [];
    for (const id of ids) {
      const rec = this.objects.get(id);
      if (!rec) continue;
      if (rec.deletedAt && !opts.includeDeleted) continue;
      out.push(this.materialize(rec, mi));
    }
    return out;
  }

  async count(scope: Scope): Promise<number> {
    return this.members(scope).length;
  }

  async query(scope: Scope, page: Page): Promise<Slice<StoredObject>> {
    const mi = this.manifestOf(scope.manifest);
    const all = this.members(scope);
    const { items, cursor } = paginate(all, page);
    return {
      items: items.map((r) => this.materialize(r, mi)),
      ...opt('cursor', cursor),
      total: all.length,
    };
  }

  async *stream(scope: Scope): AsyncIterable<StoredObject> {
    const mi = this.manifestOf(scope.manifest);
    for (const rec of this.members(scope)) yield this.materialize(rec, mi);
  }

  async projection(
    scope: Scope, page: Page, opts: { readonly expandRefs?: 0 | 1 | 2 | 3 | 4 } = {},
  ): Promise<Slice<RoleValues>> {
    const lens = this.lensFor(scope);
    const all = this.members(scope);
    const { items, cursor } = paginate(all, page);
    const depth = opts.expandRefs ?? 0;
    return {
      items: items.map((r) => this.projectOne(r, lens, depth, new Set([r.id]))),
      ...opt('cursor', cursor),
      total: all.length,
    };
  }

  /**
   * Fold the whole scope. See `ObjectReader.aggregate` for why this is here and
   * not in the renderer.
   *
   * `members(scope)` is the whole membership, deliberately — there is no page
   * parameter on this call and adding one would reintroduce the exact bug the
   * signature exists to prevent.
   */
  /** The label a value groups under, matching the renderer exactly: the
   *  projection hands it `rawOf(value)`, and the renderer formats that. */
  private static labelOf(v: Value): string | undefined {
    const raw = rawOf(v);
    if (raw === undefined || raw === null) return undefined;
    // A ref groups by its target id, which is an identity and must not become a
    // visible column heading — the same rule that keeps ids out of the DOM.
    if (v.k === 'ref') return undefined;
    const text = formatValue(raw);
    return text === '' ? undefined : text;
  }

  async aggregate(
    scope: Scope, specs: readonly AggregateSpec[], opts: { readonly groupBy?: Role } = {},
  ): Promise<AggregateResult> {
    const mi = this.manifestOf(scope.manifest);

    /** Sorted so that asking for the same questions in a different order is the
     *  same question. The fold's answer is a map keyed by role and function, so
     *  spec order cannot reach the result — including `first` and `last`, which
     *  are the SCAN order and not the spec order. */
    const cacheKey = `${scopeKey(scope)}|${opts.groupBy ?? ''}|`
      + specs.map((sp) => foldKey(sp.role, sp.fn)).sort().join(',');
    const cached = this.folds.get(cacheKey);
    if (cached) return StoreEngine.resultOf(cached);

    const plan = StoreEngine.planFold(specs, mi, scope, opts.groupBy);
    const acc: FoldAcc = {
      plan,
      total: StoreEngine.emptyBucket(plan.roles.length),
      ...(opts.groupBy ? { groups: new Map<string, FoldBucket>() } : {}),
    };

    for (const rec of this.members(scope)) this.foldIn(acc, rec, 1);

    if (this.folds.size >= StoreEngine.FOLD_CACHE_MAX) this.folds.clear();
    this.folds.set(cacheKey, acc);
    return StoreEngine.resultOf(acc);
  }

  /**
   * Everything about a fold that does not change while it is cached.
   *
   * Hoisted out of the row loop for the reason task 0056 hoisted it out of the
   * spec loop: `keysByRole` is a Map lookup that cannot change while a fold
   * runs, and it was being performed once per object per spec. It cannot change
   * while the fold is CACHED either — a manifest is content-addressed and a new
   * one is a new `scopeKey` — so it is computed once per fold rather than once
   * per read.
   */
  private static planFold(
    specs: readonly AggregateSpec[], mi: ManifestIndex, scope: Scope, groupBy?: Role,
  ): FoldPlan {
    const roles = [...new Set(specs.map((s) => s.role))];
    return {
      specs, scope, mi, ...opt('groupBy', groupBy),
      roles,
      keysOf: roles.map((role) => mi.keysByRole.get(role) ?? []),
      slotFor: new Map(roles.map((role, i) => [role, i])),
      // Whether anything asked this role for a NUMBER. `count` alone does not,
      // and the old `populated()` returned at the first populated key without
      // ever parsing a value — so without this, a board that only counts a date
      // column pays a `Date.parse` per object for an answer nobody asked for.
      numeric: roles.map((role) => specs.some((s) => s.role === role && s.fn !== 'count')),
      /**
       * Whether this fold can survive a write by being ADJUSTED.
       *
       * `count`, `sum` and `avg` are the three that can: each is a running
       * total, and a running total can have one object taken back out of it.
       * `min`, `max` and `span` cannot — removing the smallest value leaves you
       * needing the second smallest, which nothing here kept. `first` and
       * `last` are scan order, so any change to membership can move them.
       *
       * The baseline generator only ever asks for `sum` and `avg`
       * (`pickAggregates`), which is what makes the distinction worth drawing
       * rather than a reason to give up on the whole cache.
       */
      updatable: specs.every((s) => s.fn === 'count' || s.fn === 'sum' || s.fn === 'avg'),
    };
  }

  private static emptyBucket(R: number): FoldBucket {
    return {
      n: 0,
      seen: new Array<number>(R).fill(0),      // finite numbers folded
      carried: new Array<number>(R).fill(0),   // objects carrying the role at all
      sum: new Array<number>(R).fill(0),
      min: new Array<number>(R).fill(0),
      max: new Array<number>(R).fill(0),
      first: new Array<number>(R).fill(0),
      last: new Array<number>(R).fill(0),
    };
  }

  /**
   * One object's contribution, added (`sign` 1) or taken back out (`sign` -1).
   *
   * The whole fold is now expressed through this — the initial pass calls it
   * once per member, and a write calls it twice per changed object. **One code
   * path, deliberately**: two would have to agree about what a fold means, and
   * a fold that disagrees with a rescan is a wrong number presented with total
   * confidence. `aggregate-parity.test.ts` is what proves they cannot drift,
   * because it compares the adjusted fold against a fresh one.
   *
   * `min`, `max`, `first` and `last` are maintained on the way IN and are wrong
   * on the way out — which is exactly why `updatable` refuses to adjust a fold
   * that reads them.
   */
  private foldInto(plan: FoldPlan, b: FoldBucket, rec: ObjectRecord, sign: 1 | -1): void {
    const { keysOf, numeric } = plan;
    b.n += sign;
    for (let r = 0; r < keysOf.length; r++) {
      const keys = keysOf[r]!;
      let has = false;
      let n = 0;
      let total = 0;
      for (let k = 0; k < keys.length; k++) {
        const slot = rec.slots.get(keys[k]!);
        if (!slot || slot.state !== 'set' || !isPopulated(slot.value)) continue;
        has = true;
        // `count` only: nothing here needs a number, and one populated key is
        // the whole answer.
        if (!numeric[r]) break;
        const v = slot.value;
        // A `date` folds as an instant, which is what `span` needs and what
        // makes min/max mean earliest and latest. A non-finite value is not a
        // value: `valueFitsKind` accepts `NaN`, and shape derivation and
        // projection both treat it as unpopulated.
        const num = v.k === 'number' ? v.v
          : v.k === 'date' ? Date.parse(v.v)
          : Number.NaN;
        if (!Number.isFinite(num)) continue;
        if (sign === 1) {
          if (b.seen[r]! + n === 0) { b.first[r] = num; b.min[r] = num; b.max[r] = num; }
          else {
            if (num < b.min[r]!) b.min[r] = num;
            if (num > b.max[r]!) b.max[r] = num;
          }
          b.last[r] = num;
        }
        n++;
        total += num;
      }
      if (has) b.carried[r]! += sign;
      b.seen[r]! += sign * n;
      b.sum[r]! += sign * total;
    }
  }

  /** The group an object belongs to, or `undefined` when it carries no value
   *  for the grouping role and so belongs to none. */
  private groupLabelOf(plan: FoldPlan, rec: ObjectRecord): string | undefined {
    if (!plan.groupBy) return undefined;
    for (const key of plan.mi.keysByRole.get(plan.groupBy) ?? []) {
      const slot = rec.slots.get(key);
      if (!slot || slot.state !== 'set' || !isPopulated(slot.value)) continue;
      // EVERY legal grouping kind, not just enum and text. The validator
      // refuses only media and av, so a board grouped by a number, a score, a
      // date or a ref is legal — and taking only enum/text here left those
      // boards with no store-backed totals, silently falling back to the
      // painted member count, which is the exact wrong-number-under-
      // virtualization this feature exists to prevent. Found by @Codex.
      //
      // The label has to match what the RENDERER groups by, or a group's total
      // lands on a column with a different name. `rawOf` is what the projection
      // hands the renderer, so formatting from it is the only way they agree.
      const label = StoreEngine.labelOf(slot.value);
      if (label !== undefined) return label;
    }
    return undefined;
  }

  /** One object into the whole fold: the total, and its group if it has one. */
  private foldIn(acc: FoldAcc, rec: ObjectRecord, sign: 1 | -1): void {
    this.foldInto(acc.plan, acc.total, rec, sign);
    if (!acc.groups) return;
    const label = this.groupLabelOf(acc.plan, rec);
    if (label === undefined) return;
    let bucket = acc.groups.get(label);
    if (!bucket) {
      if (sign === -1) return;                 // nothing to take out of
      bucket = StoreEngine.emptyBucket(acc.plan.roles.length);
      acc.groups.set(label, bucket);
    }
    this.foldInto(acc.plan, bucket, rec, sign);
    // A group emptied by a removal is GONE, not present with a zero in it.
    // `n === 0` is the honest test: a fold with nothing in it is absent rather
    // than zero, and a board column that lost its last card should disappear
    // the way a rescan would have left it out.
    if (bucket.n === 0) acc.groups.delete(label);
  }

  /**
   * The public answer, derived from the accumulators.
   *
   * Every semantic here is the one the pre-accumulator shape had, and
   * `aggregate.test.ts` is what says so rather than this comment:
   *
   *  - a role FRAGMENTED across several keys folds all of them;
   *  - `count` counts objects CARRYING the role, whatever kind it is — which is
   *    why `carried` is tracked separately from `seen`. Answering it through
   *    the numeric extractor reported zero for every text, enum, ref and media
   *    role. Found by @Codex;
   *  - `first` and `last` are scan order, which is id order and so creation
   *    order — the same order `query` pages in, so a reader sees the same first
   *    row the fold saw;
   *  - a fold with nothing in it is ABSENT, not zero. "These films run no time
   *    at all" is a different claim from "no film here records a runtime".
   *
   * And extrema still never spread. `Math.min(...ns)` throws `RangeError` once
   * an array passes the engine's argument limit, which at 100k is reachable
   * with one fragmented role, and a fold that crashes on a large valid pool is
   * worse than a slow one — @Codex again.
   */
  private static valuesOf(plan: FoldPlan, b: FoldBucket): ReadonlyMap<string, number> {
    const values = new Map<string, number>();
    for (const spec of plan.specs) {
      const r = plan.slotFor.get(spec.role)!;
      const key = foldKey(spec.role, spec.fn);
      if (spec.fn === 'count') {
        if (b.carried[r]! > 0) values.set(key, b.carried[r]!);
        continue;
      }
      if (b.seen[r]! === 0) continue;
      switch (spec.fn) {
        case 'sum': values.set(key, b.sum[r]!); break;
        case 'avg': values.set(key, b.sum[r]! / b.seen[r]!); break;
        case 'min': values.set(key, b.min[r]!); break;
        case 'max': values.set(key, b.max[r]!); break;
        case 'span': values.set(key, b.max[r]! - b.min[r]!); break;
        case 'first': values.set(key, b.first[r]!); break;
        case 'last': values.set(key, b.last[r]!); break;
      }
    }
    return values;
  }

  private static resultOf(acc: FoldAcc): AggregateResult {
    return {
      n: acc.total.n,
      values: StoreEngine.valuesOf(acc.plan, acc.total),
      ...(acc.groups
        ? { groups: new Map([...acc.groups].map(([label, b]) =>
            [label, { n: b.n, values: StoreEngine.valuesOf(acc.plan, b) }])) }
        : {}),
    };
  }

  /**
   * A commit, applied to the folds instead of emptying them.
   *
   * The cache used to be cleared wholesale by any write, and its own comment
   * defended that as *"the cheap correct thing… the case a person feels is the
   * frame after an edit, and this makes that frame no faster."* It was right
   * about the cost: at 100k a grouped fold cost 106ms on the frame after an
   * edit, which is most of the 133ms the acceptance row is judged on.
   *
   * The adjustment is exact rather than conservative. For every object the
   * commit touched, this asks whether it was in the fold's scope BEFORE and
   * whether it is in it AFTER, and applies the difference:
   *
   *  - in → in     : take the old record out, put the new one in
   *  - out → in    : put it in (a create, or a filter it now satisfies)
   *  - in → out    : take it out (a retire, or a filter it no longer satisfies)
   *  - out → out   : nothing
   *
   * So membership needs no separate rule and no role-intersection guess:
   * `inScope` is the same predicate `members` uses, evaluated on both records.
   * A group-by change needs no rule either — the old record leaves its old
   * group and the new one joins the new group, because that is what the two
   * calls do.
   *
   * What DOES clear the cache: a change to the key registry. A `defineProperty`
   * or a `bindRole` changes what a role MEANS, so every cached plan's
   * `keysByRole` is stale and no delta could repair it.
   *
   * An ordinary fold reads slots, so an edge changes nothing it could have
   * counted. A traversal fold is different: an edge can move its membership,
   * so that fold is discarded and rebuilt on its next read.
   */
  private updateFolds(
    objects: ReadonlyMap<ObjectId, ObjectRecord | null>,
    keysChanged: boolean,
    graphChanged: boolean,
  ): void {
    if (this.folds.size === 0) return;
    if (keysChanged) { this.folds.clear(); return; }

    for (const [cacheKey, acc] of this.folds) {
      if (graphChanged && acc.plan.scope.query?.traverse) {
        this.folds.delete(cacheKey);
        continue;
      }
      if (!acc.plan.updatable) { this.folds.delete(cacheKey); continue; }
      // A BULK commit is cheaper to refold than to adjust. The adjustment costs
      // two passes over the objects it touched; a refold costs one pass over
      // the fold's members. Seeding 100,000 objects in one commit with a fold
      // already cached would otherwise walk them twice per cached fold — up to
      // sixty-four times the work of just letting go.
      if (objects.size * 2 >= acc.total.n) { this.folds.delete(cacheKey); continue; }
      const { scope, mi } = acc.plan;
      for (const [id, after] of objects) {
        const before = this.objects.get(id);
        const wasIn = before !== undefined && this.inScope(before, scope, mi);
        const isIn = after !== null && this.inScope(after, scope, mi);
        if (wasIn) this.foldIn(acc, before!, -1);
        if (isIn) this.foldIn(acc, after!, 1);
      }
    }
  }

  async traverse(t: Traversal): Promise<TraversalResult> {
    if (ROLE_KIND[t.via] !== 'ref') {
      throw new TypeError(`traverse needs a ref role; "${t.via}" is ${ROLE_KIND[t.via]}`);
    }
    const mi = this.manifestOf(t.manifest);
    const nodes = new Map<ObjectId, ObjectRecord>();
    const depthOf = new Map<ObjectId, number>();
    const edges: TraversalEdge[] = [];
    const seenEdge = new Set<string>();
    const cycles: ObjectId[][] = [];
    const started = Date.now();
    let truncated: TraversalResult['truncated'] = 'none';

    const stack: { id: ObjectId; depth: number; path: readonly ObjectId[] }[] = [];
    for (const id of t.from) {
      const rec = this.objects.get(id);
      if (!rec || rec.deletedAt) continue;
      stack.push({ id, depth: 0, path: [id] });
    }

    while (stack.length) {
      if (t.timeoutMs !== undefined && Date.now() - started > t.timeoutMs) {
        truncated = 'timeout';
        break;
      }
      const cur = stack.pop()!;
      const rec = this.objects.get(cur.id);
      if (!rec || rec.deletedAt) continue;
      if (!nodes.has(cur.id)) {
        if (nodes.size >= t.maxNodes) { truncated = 'max-nodes'; break; }
        nodes.set(cur.id, rec);
      }
      const known = depthOf.get(cur.id);
      if (known === undefined || cur.depth < known) depthOf.set(cur.id, cur.depth);
      if (cur.depth >= t.depth) { truncated = truncated === 'none' ? 'depth' : truncated; continue; }

      for (const step of this.neighbours(cur.id, t.via, t.direction, mi)) {
        const sig = `${step.from}>${step.to}`;
        if (!seenEdge.has(sig)) { seenEdge.add(sig); edges.push(step); }
        const next = step.from === cur.id ? step.to : step.from;
        // Path-based, not visited-set-based. Two siblings pointing at one object
        // are not a cycle, and treating them as one refuses ordinary shared
        // references — the false positive @Codex caught in the renderer.
        if (cur.path.includes(next)) {
          cycles.push([...cur.path.slice(cur.path.indexOf(next)), next]);
          continue;
        }
        stack.push({ id: next, depth: cur.depth + 1, path: [...cur.path, next] });
      }
    }

    return {
      nodes: [...nodes.values()].map((r) => this.materialize(r, mi)),
      edges, depthOf, truncated, cycles,
    };
  }

  subscribe(scope: Scope, listener: (e: StoreEvent) => void): Unsubscribe {
    const key = scopeKey(scope);
    let sub = this.listeners.get(key);
    if (!sub) { sub = { scope, listeners: new Set() }; this.listeners.set(key, sub); }
    const bucket = sub;
    bucket.listeners.add(listener);
    return () => {
      bucket.listeners.delete(listener);
      // The bucket owns the scope. Retaining it after its last listener leaves
      // keeps traversal closures alive and makes every later commit scan them
      // twice even though nobody can receive the event.
      if (bucket.listeners.size === 0 && this.listeners.get(key) === bucket) {
        this.listeners.delete(key);
      }
    };
  }

  /* ───────────────────────────────────────────────────────────────── shape */

  async deriveShape(scope: Scope, opts: DeriveOptions = {}): Promise<StoredShape> {
    this.ensureRegistered();
    const key = scopeKey(scope);
    const enumCap = opts.enumCardinalityCap ?? DEFAULT_ENUM_CAP;
    const maxSample = opts.maxSample ?? DEFAULT_MAX_SAMPLE;
    const live = this.digests.get(key);

    // A registered scope has exact counters over the whole collection, so it is
    // never sampled — sampling exists only to bound the rescan an ad-hoc scope
    // has to do, and the rescan is deterministic in id order so the same
    // collection samples the same way twice. A role sitting near the coverage
    // floor must not flicker between renders.
    const counters = live ?? this.scan(scope, enumCap, maxSample);
    const total = live ? live.total : this.members(scope).length;
    const n = counters.total;
    const mi = this.manifestOf(scope.manifest);

    const roles: ShapeRole[] = [];
    const enumCardinality: Record<string, number> = {};
    const orderedCardinality: Record<string, number> = {};
    for (const [role, keys] of mi.keysByRole) {
      const objects = counters.objectsByRole.get(role) ?? 0;
      if (objects === 0) continue;
      const kind = ROLE_KIND[role];
      const newest = keys
        .map((k) => this.keys.get(k)?.createdAt)
        .filter((d): d is string => d !== undefined)
        .reduce<number>((max, d) => Math.max(max, Date.parse(d)), Number.NEGATIVE_INFINITY);
      roles.push({
        role,
        kind,
        coverage: n === 0 ? 0 : objects / n,
        keyCount: keys.length,
        // The INSTANT, not an age. No threshold here on purpose — whether a
        // young key is young *enough* to be exempt from `roleCoverageFloor` is
        // a generation parameter, and a number deciding it in the store would
        // be a design value hardcoded one layer further down than params.ts can
        // see. Reading the clock here was the same mistake in the other
        // direction: it made the shape a function of WHEN it was derived.
        ...(Number.isFinite(newest)
          ? { declared: { since: new Date(newest).toISOString() } } : {}),
      });
      if (kind === 'enum') {
        const values = new Set<string>();
        for (const k of keys) for (const v of counters.enumValues.get(k)?.counts.keys() ?? []) values.add(v);
        if (values.size > 0) enumCardinality[role] = values.size;
      }
      // The ordered scalars, bounded the same way. @Codex's ruling on 0079:
      // coverage cannot say whether a threshold could partition anything,
      // because a role can be on every object and hold one value on every
      // object. A COUNT crosses the boundary and never a value.
      if (kind === 'number' || kind === 'score' || kind === 'date') {
        const values = new Set<string>();
        for (const k of keys) {
          for (const v of counters.orderedValues.get(k)?.counts.keys() ?? []) {
            if (values.size >= enumCap) break;
            values.add(v);
          }
          if (values.size >= enumCap) break;
        }
        if (values.size > 0) orderedCardinality[role] = values.size;
      }
    }
    roles.sort((a, b) => b.coverage - a.coverage);

    /**
     * Containment, per derivative role. Task `0131`.
     *
     * `true` means every object carrying the SOURCE also carries the
     * derivative, so a card that binds the derivative alone loses no picture.
     * The counter is maintained on write beside `objectsByRole`; this only
     * reads it.
     *
     * A key is emitted only where the source is present at all. Zero objects
     * carry the source means zero are missing the derivative, which would
     * report `true` — a claim about a population that does not exist, and the
     * kind of vacuous truth a consumer acts on wrongly. Absent reads as "not
     * known", which is what it is.
     */
    const derivativeCovers: Record<string, boolean> = {};
    /**
     * A SAMPLED scan cannot prove `true` — @Codex, P2 on `18eae3f`. An
     * unregistered scope over more objects than `maxSample` scans a prefix,
     * and a containment fact derived from the prefix was exported as a claim
     * about the population: object 501 carrying only the source lost its
     * media when generation took `true` as permission to bind the derivative
     * alone. One counterexample proves `false` from any sample, so a sampled
     * `false` stands; a sampled zero-missing is "not known", which absent
     * already means. Registered scopes keep exact counters and are never
     * sampled, so they still report both values.
     */
    const sampledPrefix = !live && counters.total < total;
    for (const [derivative, source] of Object.entries(DERIVATIVE_OF)) {
      if (!counters.objectsByRole.get(source as Role)) continue;
      const missing = (counters.sourceWithoutDerivative.get(derivative as Role) ?? 0) > 0;
      if (missing) derivativeCovers[derivative] = false;
      else if (!sampledPrefix) derivativeCovers[derivative] = true;
    }

    // JOINT counts, folded from key pairs up to role pairs.
    //
    // The sketches are keyed by KEY pair and ordered by key, so the two halves
    // of a value pair arrive in key order — which is not role order, and two
    // key pairs carrying one role pair can disagree about it. Re-ordered by
    // role before the union, or the same cross would be counted twice under
    // two spellings and a correlated pool would look like a divided one.
    const roleOfKey = new Map<PropertyKeyId, Role>();
    for (const [role, keys] of mi.keysByRole) for (const k of keys) roleOfKey.set(k, role);
    const pairValues = new Map<string, Set<string>>();
    for (const [pk, sketch] of counters.enumPairs) {
      const [ka, kb] = pk.split('|') as [PropertyKeyId, PropertyKeyId];
      const ra = roleOfKey.get(ka);
      const rb = roleOfKey.get(kb);
      if (ra === undefined || rb === undefined || ra === rb) continue;
      if (ROLE_KIND[ra] !== 'enum' || ROLE_KIND[rb] !== 'enum') continue;
      const key = enumPairKey(ra, rb);
      let set = pairValues.get(key);
      if (!set) { set = new Set(); pairValues.set(key, set); }
      const swap = (ra < rb) !== (ka < kb);
      for (const v of sketch.counts.keys()) {
        if (!swap) { set.add(v); continue; }
        const at = v.indexOf('\u0000');
        set.add(`${v.slice(at + 1)}\u0000${v.slice(0, at)}`);
      }
    }
    const enumPairCardinality: Record<string, number> = {};
    for (const [k, set] of pairValues) enumPairCardinality[k] = set.size;

    return {
      // Same thresholds as `generation/src/shape.ts`, because the two derive the
      // same statistic and a generator cannot be handed one of each.
      cardinality: total <= 1 ? 'single' : total <= 40 ? 'small-set' : 'large-set',
      // `av` and `doc` count. Both shape-derivation paths compared kinds by
    // VALUE, and both were short: first of `av`, so a pool of nothing but video
    // derived `hasMedia: false` and got `mediaWeight: 'text'`, and then of
    // `doc`, so a pool of nothing but PDFs did the same — @Codex found each.
    //
    // Read from `VISUAL_KINDS` now rather than restated. The list existed the
    // whole time as `baseline.ts`'s private `isVisual`, correct and including
    // `doc`, under a docstring saying it was *"named once so the next kind
    // added cannot repeat it"*. A name that two other packages cannot import is
    // not named once.
    hasMedia: roles.some((r) => isVisualKind(r.kind) && r.coverage > 0),
      // OBJECTS carrying a reference, over objects — the same shape as
      // `crossDocDensity` below, whose comment has always carried the argument
      // for both: *"objects rather than references … a card with three
      // references into one note is still one card with a relation to draw."*
      //
      // Two defects, one line. It divided by TEN, so a pool where every object
      // holds exactly one reference scored 0.1 against a threshold of 0.2 and
      // **a film with a Filmmaker never surfaced one**. And it summed EDGES, so
      // one object with five references in a pool of twenty scored 0.25 and
      // crossed the same threshold while 95% of the pool had nothing to draw.
      // The second was found by @Codex, after the first was fixed and the tests
      // written for it could not tell the two apart.
      relationDensity: n === 0 ? 0 : Math.min(counters.relationObjects / n, 1),
      // Objects with somewhere else to point, over objects. Deliberately NOT
      // scaled by ten the way `relationDensity` is: one card in ten holding a
      // reference into another document is a real share of this lens, not a
      // tenth of one, and a number a generator reads should mean what it says.
      crossDocDensity: n === 0 ? 0 : counters.crossDocTotal / n,
      ...(Object.keys(enumCardinality).length ? { enumCardinality } : {}),
      ...(Object.keys(orderedCardinality).length ? { orderedCardinality } : {}),
      ...(Object.keys(enumPairCardinality).length ? { enumPairCardinality } : {}),
      // Task `0131`. Emitted whenever this deriver ran, empty or not, so a
      // consumer can tell "no source present" from "this shape does not carry
      // the fact" — a shape assembled elsewhere has the field absent entirely.
      derivativeCovers,
      roles,
      n,
      total,
      sampled: !live && total > n,
      scopeKey: key,
      digestRevision: asRevision(counters.revision),
    };
  }

  async enumOptions(scope: Scope, role: Role): Promise<readonly string[]> {
    this.ensureRegistered();
    const mi = this.manifestOf(scope.manifest);
    const counters = this.digests.get(scopeKey(scope)) ?? this.scan(scope, DEFAULT_ENUM_CAP);
    // Summed across every key carrying the role, because two keys may both
    // carry it and a chooser offering one key's values would silently hide the
    // other's — the same fragmentation `ShapeRole.keyCount` records.
    const counts = new Map<string, number>();
    for (const key of mi.keysByRole.get(role) ?? []) {
      for (const [value, n] of counters.enumValues.get(key)?.counts ?? []) {
        counts.set(value, (counts.get(value) ?? 0) + n);
      }
    }
    // Commonest first, ties broken by name so the order is stable across
    // renders — a dropdown that reshuffles between repaints is unusable.
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([value]) => value);
  }

  async shapeDigest(scope: Scope): Promise<ShapeDigest> {
    this.ensureRegistered();
    const key = scopeKey(scope);
    return toDigest(this.digests.get(key) ?? this.scan(scope, DEFAULT_ENUM_CAP), key);
  }

  /** Full rescan. The parity witness for the incremental counters — 0005 asks
   *  for exactly this, and without it a drifted digest is silently wrong. */
  async rebuildDigest(scope: Scope): Promise<ShapeDigest> {
    this.ensureRegistered();
    const key = scopeKey(scope);
    const fresh = this.scan(scope, DEFAULT_ENUM_CAP);
    if (this.digests.has(key)) {
      fresh.revision = this.digests.get(key)!.revision;
      this.digests.set(key, fresh);
    }
    return toDigest(fresh, key);
  }

  private scan(scope: Scope, enumCap: number, maxSample = Number.POSITIVE_INFINITY): Counters {
    const counters = new Counters(scope, scopeKey(scope), enumCap);
    const lens = this.lensFor(scope);
    const members = this.members(scope);
    const capped = members.length > maxSample ? members.slice(0, maxSample) : members;
    for (const rec of capped) counters.add(rec.id, this.contributionOf(rec, lens));
    counters.revision = this.digestClock;
    return counters;
  }

  /** What one object adds to a scope's counters. Computed once and applied with
   *  a sign, so removing an object cannot disagree with having added it. */
  private contributionOf(rec: ObjectRecord, lens: Lens): Contribution {
    const { mi } = lens;
    const populated = new Set<PropertyKeyId>();
    const enums: (readonly [PropertyKeyId, string])[] = [];
    const ordered: (readonly [PropertyKeyId, string])[] = [];
    // Edges and ref-valued slots are both relations. Counting only one of them
    // would make `relationDensity` depend on how a relation happened to be
    // recorded, which is not something a layout decision may turn on.
    let relations = this.outEdges.get(rec.id)?.size ?? 0;
    // The same rule applied to the cross-doc count: an edge into a declared
    // document is the same relation as a ref-valued slot into one.
    let crossDoc = 0;
    for (const eid of this.outEdges.get(rec.id) ?? []) {
      const to = this.edges.get(eid)?.to;
      if (to !== undefined && this.leavesTheDocument(to, lens)) crossDoc += 1;
    }

    for (const [key, slot] of rec.slots) {
      if (slot.state !== 'set' || !isPopulated(slot.value)) continue;
      populated.add(key);
      const kind = kindOf(slot.value);
      if (kind === 'enum') enums.push([key, String(rawOf(slot.value))]);
      if (kind === 'number' || kind === 'score' || kind === 'date') {
        ordered.push([key, String(rawOf(slot.value))]);
      }
      if (kind === 'ref') {
        relations += 1;
        if (slot.value.k === 'ref' && this.leavesTheDocument(slot.value.v, lens)) crossDoc += 1;
      }
    }

    // A role this object carries as an EDGE rather than as a ref slot.
    //
    // Decision 0016 names this a blocking prerequisite for relation mutations,
    // and calls the state it replaces — reading only the first ref slot —
    // *"not an acceptable compatibility state"*. The divergence was inside
    // this one function: an edge already counted toward `relations` two blocks
    // up, on the stated grounds that *"counting only one of them would make
    // `relationDensity` depend on how a relation happened to be recorded"* —
    // and then `roles` was built from populated SLOTS alone, which is exactly
    // that dependency, one field over.
    //
    // Measured before the fix, on a pool whose only relation is an edge:
    // `deriveShape` reported `[title]` with no `relation.peer`, while
    // `relationDensity` was 0.05 and `projection` returned the relation.
    // `apps/demo/src/store-setup.ts` had already written the symptom down —
    // *"edges alone are invisible to it"* — and worked around it by making the
    // first citation fill a ref slot so the role would appear at all.
    //
    // NOT gated here. The loop below iterates `mi.keysByRole`, so a role the
    // reading manifest does not name is never reached whatever this set holds
    // — the same structure that makes `projectOne` read edges only for roles
    // the manifest names, and the reason task 0049's boundary still holds.
    //
    // The first version DID gate here as well, and a mutation removing that
    // gate failed nothing: it was dead code, and the test written for it was
    // vacuous. Both are gone. The boundary is asserted against the loop that
    // actually enforces it.
    // The EDGES themselves rather than the public words on them. An edge is
    // addressed by its private key now, so "does this manifest see it" is a
    // question about the key — and a set of roles cannot answer it. Two of a
    // tenant's relations sharing the word `relation.peer` used to make each
    // one visible through the other's manifest, here as everywhere else.
    const outgoing: EdgeRecord[] = [];
    for (const eid of this.outEdges.get(rec.id) ?? []) {
      const e = this.edges.get(eid);
      if (e) outgoing.push(e);
    }

    const roles: Role[] = [];
    for (const [role, keys] of mi.keysByRole) {
      if (keys.some((k) => populated.has(k))
        || outgoing.some((e) => edgeIsUnder(e, role, keys))) roles.push(role);
    }
    return { populated: [...populated], roles, enums, ordered, relations, crossDoc };
  }

  /** Whether a reference points OUT of the lens's own document — the target is
   *  not addressed by the scope's manifest, and is addressed by one the scope
   *  declared. A reference into a document nobody declared does not count: it
   *  resolves to nothing, so a region drawn for it would be an empty box. */
  private leavesTheDocument(to: ObjectId, lens: Lens): boolean {
    if (lens.across.length === 0) return false;
    const target = this.objects.get(to);
    if (!target || target.deletedAt || this.addresses(target, lens.mi)) return false;
    return lens.across.some((m) => this.addresses(target, m));
  }

  /* ───────────────────────────────────────────────────────────── authority */

  /**
   * Mint an authority from the spec that will actually be rendered.
   *
   * The order matters. Validation first, because a spec that does not validate
   * has bindings nobody has checked for direction — and `authorize` is the only
   * place that check is load-bearing. Then one pass over the tree, and a slot
   * ends up in exactly one of three places: `writable` (a grant can be minted),
   * `refused` (it asked for a write the store will not perform, with the
   * reason), or `readOnly` (it never asked).
   */
  async authorize(
    spec: TLayoutSpec, manifest: ManifestRef, actor?: Actor,
  ): Promise<BindingAuthority | Refusal> {
    if (this.closed) return refuse('closed', 'the store is closed');
    if (!this.manifests.has(manifest)) {
      return refuse('unknown-manifest', `manifest "${manifest}" is not known to this store`);
    }
    const result = validateSpec(spec);
    if (!result.valid) {
      return refuse('invalid-spec',
        'the spec does not validate, so its bindings cannot be trusted to declare a '
        + `direction: ${result.problems.map((p) => `${p.path} ${p.code}`).join(', ')}`);
    }

    const writable = new Map<string, WriteBinding>();
    const readOnly = new Set<string>();
    const refused = new Map<string, Refusal>();
    const roleOfSlot = new Map<string, Role>();

    for (const { slot, binding, sorted } of collectBindings(spec.root)) {
      const priorRole = roleOfSlot.get(slot);
      if (priorRole !== undefined && priorRole !== binding.role) {
        writable.delete(slot);
        readOnly.delete(slot);
        refused.set(slot, refuse('ambiguous-role',
          `slot "${slot}" binds both "${priorRole}" and "${binding.role}", so a write to it `
          + 'has no single destination'));
        continue;
      }
      roleOfSlot.set(slot, binding.role);
      if (refused.has(slot)) continue;

      if (binding.direction === 'read') { readOnly.add(slot); continue; }
      if (!binding.mutation) {
        refused.set(slot, refuse('undeclared-mutation',
          `slot "${slot}" is read-write and declares no mutation`));
        continue;
      }
      if (!MUTATION_ROLE_KINDS[binding.mutation].includes(ROLE_KIND[binding.role])) {
        refused.set(slot, refuse('mutation-role-mismatch',
          `"${binding.mutation}" is not legal against a ${ROLE_KIND[binding.role]} role `
          + `("${binding.role}")`));
        continue;
      }
      if (binding.mutation === 'reorder-within' && sorted) {
        // 0005 forbids the store to hold presentation state, and a position
        // inside a sorted collection is exactly that: the order is a function
        // of the sort key, so there is no property a drag could write. The
        // user-visible symptom is a drag that does nothing, which is why the
        // refusal has to name itself rather than fail silently.
        refused.set(slot, refuse('order-not-semantic',
          `slot "${slot}" reorders inside a sorted collection, where position is derived `
          + 'from the sort and is not a value any object holds'));
        continue;
      }
      writable.set(slot, {
        ...binding, direction: 'read-write', mutation: binding.mutation, [WRITABLE]: true,
      } as WriteBinding);
    }

    const relationSlots = new Map<string, RelationSlot>();
    for (const { id, via, mutations } of collectRelationSlots(spec.root)) {
      // Both legality tables again, on this side of the boundary. The validator
      // has already refused an illegal declaration — `authorize` runs
      // `validateSpec` first — so this cannot fire for a spec that got here;
      // it fires for a spec that got here another way, which is exactly the
      // case decision 0007 says the store may not take on trust.
      if (!mutations.every((m) => mutationAcceptsRole(m, via))) {
        refused.set(id, refuse('mutation-role-mismatch',
          `"${via}" is a ${ROLE_KIND[via]} but not a relation role, so a node slot over it `
          + 'cannot be attached to'));
        continue;
      }
      relationSlots.set(id, { role: via, mutations });
    }

    return {
      [WRITABLE]: true,
      manifest,
      specHash: stableHash(spec),
      writable, readOnly, refused, relationSlots,
      ...opt('actor', actor),
    };
  }

  async authorizeEditor(actor: Actor): Promise<EditorAuthority | Refusal> {
    if (this.closed) return refuse('closed', 'the store is closed');
    if (this.policy && !(await this.policy.canCreate(actor))) {
      return refuse('not-editable', `actor "${actor.id}" may not create objects or properties`);
    }
    return { [EDITOR]: true, actor };
  }

  async putManifest(a: EditorAuthority, entries: readonly ManifestEntry[]): Promise<ManifestRef> {
    // A manifest is what gives a slot its meaning, so minting one is a schema
    // change and sits behind the same door as the rest of them. It throws rather
    // than refusing because the signature promises a ref: a caller that ignored
    // a refusal here would carry an undefined manifest into every later read.
    if (!isEditorAuthority(a)) throw new TypeError(FORGED);
    return this.storeManifest(entries);
  }

  /** Copy-on-write. Extending returns a NEW ref, so a spec already compiled
   *  against the old one never has the meaning of its slots changed under it —
   *  which would silently re-point a live write binding at another property. */
  async extendManifest(
    a: EditorAuthority, base: ManifestRef, entries: readonly ManifestEntry[],
  ): Promise<ManifestRef> {
    if (!isEditorAuthority(a)) throw new TypeError(FORGED);
    const prior = this.manifests.get(base);
    if (!prior) throw new Error(`manifest "${base}" is not known to this store`);
    return this.storeManifest([...prior.entries, ...entries]);
  }

  /**
   * Tell whoever is listening that the durable revision moved.
   *
   * Called from EVERY path that advances `storeRevision`, which is the second
   * half of @Codex's finding. P2 on #166: *"`putManifest` and `extendManifest`
   * commit directly through `persistence.commit` in `storeManifest` and advance
   * `storeRevision` without calling it. A schema or manifest change can
   * therefore durably move the shared database without producing any
   * invalidation, leaving another open tab on its old manifest until some
   * unrelated ordinary write or a reload occurs."*
   *
   * And it CANNOT change the outcome of a commit the backend has taken. P1, on
   * the same seam: *"the backend has already accepted the patch, but this
   * method rejects before emitting store events or returning `{ ok: true }`.
   * The caller therefore sees a failed write that actually persisted and may
   * retry a non-idempotent edit against altered state."*
   *
   * Reachable: `announceOn` posts to a `BroadcastChannel`, and posting on a
   * closed one throws `InvalidStateError`. Swallowed rather than reported
   * because there is no error channel at this layer and the notice is
   * best-effort by nature — nothing is listening when a person has one tab
   * open. What must not be best-effort is the answer a commit returns about
   * durable state.
   */
  private announceCommitted(): void {
    try {
      this.onCommitted?.(this.storeRevision);
    } catch { /* a notice nobody heard is not a failed write */ }
  }

  /**
   * The vocabulary check, the dedup and the content-addressed ref — in ONE
   * place, because two callers now need them.
   *
   * `storeManifest` had these inline until task `0126` folded a manifest
   * extension into `edit`'s commit. A second copy of the vocabulary check is
   * exactly the drift decision `0044` §1 is about, one package out: the point
   * of naming a role from a closed set is defeated by a path that names it
   * somewhere else.
   *
   * Pure, and it commits nothing. What differs between the two callers is when
   * the row reaches the backend and when the in-memory index is updated, which
   * is the whole of `0126`.
   */
  private manifestRow(entries: readonly ManifestEntry[]): {
    ref: ManifestRef; deduped: ManifestEntry[];
  } {
    const deduped: ManifestEntry[] = [];
    const seen = new Set<string>();
    for (const e of entries) {
      // A manifest role must come from the CLOSED vocabulary. Nothing checked
      // it, so `putManifest` accepted any string and it flowed straight into
      // `deriveShape` — and a derived shape is the generator's input, which is
      // the privacy boundary. An adversarial probe put
      // `patient-ssn-078-05-1120` in a manifest and read it back off a shape.
      //
      // Same class as the four leaks already closed in packages/spec, one
      // package further out: a field nobody enumerated carrying content. The
      // fix is the same one every time — name it from a closed set rather than
      // filter it.
      if (!(ROLES as readonly string[]).includes(e.role)) {
        throw new TypeError(`"${String(e.role)}" is not a role in the closed vocabulary`);
      }
      const sig = `${e.role} ${e.key}`;
      if (seen.has(sig)) continue;
      seen.add(sig);
      deduped.push(e);
    }
    // Content-addressed, so two callers building the same map land on one ref
    // instead of two refs that are equal and not identical.
    const sorted = [...deduped].sort(
      (a, b) => `${a.role}${a.key}`.localeCompare(`${b.role}${b.key}`));
    const ref = `mf_${stableHash(sorted)}` as ManifestRef;

    /**
     * A COLLISION is not a match, and `stableHash` is 32 bits.
     *
     * @Codex, P2 on #184: *"When `publishRole()` produces a manifest whose
     * 32-bit `stableHash` collides with an existing manifest, this new atomic
     * path persists the new row under the existing ref and then unconditionally
     * replaces the in-memory index. Every scope still carrying that ref
     * consequently begins resolving roles through the unrelated new key map,
     * silently changing previously stored objects' schema."*
     *
     * Correct, and it is two bugs with one cause rather than one. The atomic
     * path `set`s the index unconditionally, so a collision REPLACES a live key
     * map. `storeManifest` guards with `!this.manifests.has(ref)`, so the same
     * collision makes it store nothing and hand back a ref indexing somebody
     * else's manifest — quieter and just as wrong.
     *
     * FNV-1a over 32 bits is not a content address for a value an attacker or
     * an unlucky vault can influence; it is a bucket. Both callers were reading
     * "the ref exists" as "the same manifest exists", which is only true of a
     * collision-resistant digest.
     *
     * The check is here rather than at either call site because that is what
     * makes it one check: `manifestRow` is the single place a ref is minted,
     * which is also why the closed-vocabulary check lives here. It THROWS, like
     * that one, and for the same reason — the edit path calls this BEFORE the
     * commit, so an exception refuses the whole edit rather than half of it,
     * and refusing after the backend has taken the patch is exactly what
     * `applyCommit`'s own comments forbid.
     *
     * Widening the digest was the other option @Codex offered. It is the better
     * end state and it is a migration: `mf_` refs are persisted in scopes and
     * in objects, so changing the function orphans every stored one. Rejecting
     * the collision is correct under either digest and does not block that.
     */
    const held = this.manifests.get(ref);
    if (held) {
      /**
       * Compared CANONICALLY, on both sides.
       *
       * The stored entries keep insertion order — `indexManifest` pushes
       * `keysByRole` in that order, so a caller reading the first key of a role
       * would see it change — and the ref is computed from a sorted copy
       * precisely so *"two callers building the same map land on one ref"*.
       * Comparing the sorted candidate against unsorted stored entries would
       * therefore report a collision for the same manifest assembled in a
       * different order, which is the case content addressing exists to merge.
       *
       * Caught here rather than by a test: the first version of this check
       * returned `sorted` as the deduped entries, which fixed the comparison by
       * silently reordering what gets persisted.
       */
      const canon = (es: readonly ManifestEntry[]) => [...es]
        .sort((a, b) => `${a.role}${a.key}`.localeCompare(`${b.role}${b.key}`))
        .map((e) => `${e.role} ${e.key}`).join('\n');
      if (canon(held.entries) !== canon(sorted)) {
        throw new Error(
          `manifest ref "${ref}" already indexes a different manifest — `
          + `${held.entries.length} entries against ${sorted.length}. `
          + 'A 32-bit stableHash collision must not replace a live key map, '
          + "because every scope holding that ref would resolve its objects' "
          + 'roles through the wrong one',
        );
      }
    }
    return { ref, deduped };
  }

  private async storeManifest(entries: readonly ManifestEntry[]): Promise<ManifestRef> {
    const { ref, deduped } = this.manifestRow(entries);
    if (!this.manifests.has(ref)) {
      /**
       * Indexed AFTER the backend takes it, never before.
       *
       * This set the index first, and a commit that THROWS then left a ref
       * readable in this engine that the backend had never seen — a role that
       * resolves here and nowhere else. Worse than a transient: the ref is
       * content-addressed, so the guard above short-circuits every later
       * attempt with the same entries, and the manifest is never written at
       * all. Measured: after a throwing commit, a second `putManifest` with the
       * same entries performed **zero** commits.
       *
       * `applyCommit` guards exactly this case twenty lines away and says why —
       * *"the batch is already in the maps and the backend never took it. Put
       * the engine back to what the backend actually holds before the error
       * leaves."* It needs a `catch` and a rehydrate because its mutations are
       * speculative by design; this needs neither, because nothing between the
       * set and the commit reads the index. Not speculating is the smaller fix.
       *
       * Found by looking for it: task `0126` moved the folded path's index
       * update for this reason, and this is its sibling.
       */
      /**
       * A manifest is a fact, so it is committed conditionally like any other
       * — and it is the one write here that may safely be RETRIED rather than
       * handed back as `stale`.
       *
       * The ref is content-addressed: the same entries always produce the same
       * ref, so re-running this after a refresh writes the identical row. That
       * is exactly the property decision 0027 says an `unlink`-plus-`link` plan
       * lacks, which is why those are refused and this is not.
       */
      const rows = {
        manifests: [{ ref, entries: deduped.map((e) => ({ role: e.role, key: e.key as string })) }],
      };
      const first = await this.persistence.commit(rows, {
        expect: this.storeRevision, next: this.storeRevision + 1,
      });
      if (first.ok) {
        this.storeRevision += 1;
        this.manifests.set(ref, indexManifest(ref, deduped));
        this.ensureRegistered();
        this.announceCommitted();
      } else {
        // REPLACEMENT, like every other stale write. A merging hydrate left
        // facts the winner had deleted alive in this engine, and the retry then
        // returned success with that stale state visible — @Codex, P1 on #164.
        await this.hydrate({}, true);
        /**
         * And CHECK FOR A COLLISION AGAIN, against what the hydrate loaded.
         *
         * @Codex, P2 on `78f39a7`: *"Fresh evidence after adding the collision
         * check is that this stale-write retry bypasses it: if another engine
         * commits a different manifest with the same 32-bit ref after
         * `manifestRow()` runs, the first commit is refused, `hydrate()` loads
         * the colliding row, and this second commit overwrites it without
         * comparing contents again. Every scope using the winner's ref can then
         * resolve through the retrying engine's unrelated manifest."*
         *
         * Right, and it is the collision check landing one round short in the
         * same way two other fixes did today: the guard was put where the
         * write is DECIDED and this path decides twice. The refusal that makes
         * the first commit stale is precisely the evidence that somebody else
         * moved, so it is the one moment the check most needs re-running.
         *
         * `manifestRow` is deterministic and content-addressed, so this
         * recomputes the identical ref and deduped entries; the only thing it
         * can do differently is throw, which is the point. A 32-bit ref means
         * this is rare and not impossible, and the consequence is a scope
         * resolving through somebody else's manifest.
         */
        this.manifestRow(entries);
        const again = await this.persistence.commit(rows, {
          expect: this.storeRevision, next: this.storeRevision + 1,
        });
        if (!again.ok) throw new Error('the manifest could not be written: the store moved twice');
        this.storeRevision += 1;
        this.manifests.set(ref, indexManifest(ref, deduped));
        this.ensureRegistered();
        this.announceCommitted();
      }
    }
    return ref;
  }

  /* ────────────────────────────────────────────────────────────────  write */

  async write(
    a: BindingAuthority, basis: number, fn: (s: BoundSession) => Promise<void> | void,
  ): Promise<CommitResult> {
    if (!isBindingAuthority(a)) return refuse('not-editable', FORGED);
    if (this.closed) return refuse('closed', 'the store is closed');
    const mi = this.manifests.get(a.manifest);
    if (!mi) return refuse('unknown-manifest', `manifest "${a.manifest}" is not known`);

    const minted = new Set<AnyGrant>();
    const pending: Instruction[] = [];
    let failure: Refusal | undefined;

    const session: BoundSession = {
      reader: this,
      grant: async (slot, object) => {
        const g = await this.mintGrant(a, mi, slot, object);
        if (!isRefusal(g)) minted.add(g);
        return g;
      },
      relationGrant: async (node, object, mutation) => {
        const g = await this.mintRelationGrant(a, mi, node, object, mutation);
        if (!isRefusal(g)) minted.add(g);
        // The mutation is a runtime value in here and a type parameter at the
        // call site. `mintRelationGrant` sets `mutation` to exactly the value
        // it was handed, so the two agree — and this line is the only place
        // that fact is not checkable.
        return g as Grant<typeof mutation> | Refusal;
      },
      apply: (i) => {
        // A grant from another session, or a forged object literal, is the one
        // hole the type system cannot close: `Grant` is unforgeable outside this
        // package, but a grant minted against a *different* authority would
        // otherwise be accepted here.
        if (!minted.has(i.grant as AnyGrant)) {
          failure ??= refuse('undeclared-mutation',
            'the instruction carries a grant this session did not mint');
          return;
        }
        pending.push(i);
      },
    };

    await fn(session);
    if (failure) return failure;
    if (pending.length === 0) return this.emptyCommit('binding', a.specHash);

    const at = this.now();
    const working = new Map<ObjectId, Map<PropertyKeyId, PropertySlot>>();
    const priors = new Map<ObjectId, ObjectRecord>();
    // Which keys this commit wrote, per object. Undo restores exactly these, so
    // undoing it cannot revert a later write to a different property.
    const wrote = new Map<ObjectId, Set<PropertyKeyId>>();
    const steps: InverseStep[] = [];
    const changedRoles = new Set<Role>();
    let expressible = true;
    const conflicts: ObjectId[] = [];

    // Relation instructions leave the value loop entirely: their payload is a
    // handle rather than a `Value`, and their effect is set membership rather
    // than a slot. Split first so neither path has to keep testing which it is.
    const relational = pending.filter(
      (i): i is Extract<Instruction, { mutation: 'link' | 'unlink' }> =>
        i.mutation === 'link' || i.mutation === 'unlink');
    const valued = pending.filter((i) => i.mutation !== 'link' && i.mutation !== 'unlink');

    const edgeEdits = new Map<EdgeId, EdgeRecord | null>();
    const restoredEdges: EdgeRecord[] = [];
    const absentEdges: EdgeId[] = [];
    let linked = 0;
    let unlinked = 0;

    for (const i of relational) {
      const g = i.grant;
      const source = this.objects.get(g.object);
      if (!source || source.deletedAt) {
        return refuse('unknown-object', `object "${g.object}" does not exist`);
      }
      if (source.revision !== g.revision) { conflicts.push(g.object); continue; }

      const target = this.resolveTarget(i.target);
      if (isRefusal(target)) return target;
      const to = this.objects.get(target.object);
      if (!to || to.deletedAt) {
        return refuse('unknown-object', `object "${target.object}" does not exist`);
      }

      // THE TUPLE. Decision 0016: identity is
      // `(source, private relation key, target, anchor?)`, and *"the absence of
      // an anchor is also an exact identity value"* — so `unlink` of the
      // unanchored member must not remove an anchored one. `?? undefined` on
      // both sides rather than a truthiness test, for exactly that.
      const sameTuple = (e: EdgeRecord | null | undefined): e is EdgeRecord => !!e
        && e.from === g.object && e.key === g.key && e.to === target.object
        && (e.anchor ?? undefined) === (target.anchor ?? undefined);

      // Committed state AND what this batch has already done. Checking only the
      // store is the shape the batch itself defeats: two identical `link`
      // instructions in one `write` both see an empty set and both apply, which
      // is precisely the *"repeated grants or retries accumulating invisible
      // duplicate edges"* 0016 asks to be prevented.
      //
      // Found by mutation. Ignoring the anchor in this comparison killed the
      // unanchored-unlink assertion and SPARED its neighbour, which is the tell
      // that the neighbour was passing for another reason — it was: the two
      // links it makes are in one commit, so the anchor was never what
      // distinguished them.
      const existing = [...(this.outEdges.get(g.object) ?? [])]
        .map((eid) => this.edges.get(eid))
        .find(sameTuple)
        ?? [...edgeEdits.values()].find(sameTuple);
      // ...unless this batch already removed it. A detach followed by an attach
      // of the same tuple is two real changes, not an attach that no-ops.
      const live = existing !== undefined
        && !(edgeEdits.has(existing.id) && edgeEdits.get(existing.id) === null);

      /**
       * A relation recorded as a ref-valued SLOT rather than as an edge.
       *
       * The relation SET is the union of both — `projectOne`, `neighbours` and
       * `deriveShape` have all read it that way since task 0052, and decision
       * 0016 assumes it: *"removing the last slot-backed ref must not make a
       * role disappear while normalized edges of that role remain."* A detach
       * that only knew about edges would report success and change nothing,
       * which is an affordance with nothing behind it — the failure this repo
       * refuses by name.
       *
       * Driven, not reasoned: the demo seeds a task's first reference into a
       * slot and the rest as edges, so pressing Remove on the seeded one said
       * "reference removed" and left it on screen.
       */
      const slotRef = ((): PropertyKeyId | undefined => {
        if (target.anchor !== undefined) return undefined;   // a slot ref carries its own
        const cur = working.get(g.object)?.get(g.key) ?? source.slots.get(g.key);
        return cur?.state === 'set' && cur.value.k === 'ref' && cur.value.v === target.object
          ? g.key : undefined;
      })();

      if (i.mutation === 'unlink' && !existing && slotRef !== undefined) {
        let slots = working.get(g.object);
        if (!slots) {
          slots = new Map(source.slots);
          working.set(g.object, slots);
          priors.set(g.object, source);
          wrote.set(g.object, new Set());
        }
        wrote.get(g.object)!.add(slotRef);
        // Emptied, not dropped: the property still belongs to this object, it
        // just points at nothing now. `drop` is a schema change and lives
        // behind the other door.
        slots.set(slotRef, { state: 'empty', updatedAt: at });
        unlinked += 1;
        changedRoles.add(g.role);
        expressible = false;
        continue;
      }

      if (i.mutation === 'link') {
        // Idempotent. `link` of a member already in the set is a SUCCESSFUL
        // no-op — and, because it records nothing, an undo cannot then delete a
        // relation the person never created. Write-side uniqueness is new work:
        // the store used to mint a fresh EdgeId per identical call and
        // deduplicate only while traversing, so repeated grants accumulated
        // invisible duplicates.
        if (live) continue;
        const edge: EdgeRecord = {
          id: newEdgeId(), from: g.object, key: g.key, via: g.role, to: target.object,
          ...opt('anchor', target.anchor), at,
        };
        edgeEdits.set(edge.id, edge);
        absentEdges.push(edge.id);
        linked += 1;
      } else {
        if (!existing || !live) continue;
        edgeEdits.set(existing.id, null);
        // Only a COMMITTED edge is worth restoring: one this same batch added
        // will simply not be added when the commit is undone.
        if (this.edges.has(existing.id)) restoredEdges.push(existing);
        else absentEdges.splice(absentEdges.indexOf(existing.id), 1);
        unlinked += 1;
      }
      changedRoles.add(g.role);
      // A relation write has no inverse INSTRUCTION: rebuilding one would mean
      // minting a grant and a fresh target handle at undo time, against an
      // authority that may no longer exist. The edge snapshots above are what
      // `undo` restores from, which is the same route every non-scalar write in
      // this store already takes.
      expressible = false;
    }

    for (const i of valued) {
      const g = i.grant;
      const original = this.objects.get(g.object);
      if (!original || original.deletedAt) {
        return refuse('unknown-object', `object "${g.object}" does not exist`);
      }
      // Optimistic on `revision`, always against the COMMITTED revision — a
      // second instruction in the same batch must not read as a conflict with
      // the first.
      if (original.revision !== g.revision) { conflicts.push(g.object); continue; }
      const keyRec = this.keys.get(g.key);
      if (!keyRec) return refuse('unknown-slot', `property key "${g.key}" does not exist`);

      let slots = working.get(g.object);
      if (!slots) {
        slots = new Map(original.slots);
        working.set(g.object, slots);
        priors.set(g.object, original);
        wrote.set(g.object, new Set());
      }
      wrote.get(g.object)!.add(g.key);

      const next = this.valueFor(i, slots, keyRec);
      if (isRefusal(next)) return next;

      const before = slots.get(g.key);
      if (before?.state === 'set' && isScalarMutation(i.mutation)) {
        steps.push({
          object: g.object, key: g.key, role: g.role, mutation: i.mutation,
          before: before.value, revision: original.revision,
        });
      } else {
        // Nothing in the mutation vocabulary says "make this slot absent
        // again", so a write that CREATED a slot has no inverse instruction.
        //
        // This also covers a write over an ALREADY-EMPTY slot, and covered it
        // before clearing existed: the state is not `set`, so no step is
        // recorded and undo takes the snapshot. Worth stating because the
        // opposite was reported as a hazard during task 0096 — the inverse is
        // rebuilt by stringifying `before`, which would fabricate a value if a
        // step could ever carry an empty one. It cannot; this branch is why.
        expressible = false;
      }
      // `clear` produces `empty`, NOT `absent`. The slot, its key and the
      // collection's shape all survive — which is the whole of @Codex's ruling
      // on 0096, and the distinction the demo bridge collapsed when it refused
      // this as though it were `drop`.
      slots.set(g.key, next === CLEAR
        ? { state: 'empty', updatedAt: at }
        : { state: 'set', value: next, updatedAt: at });
      changedRoles.add(g.role);
    }

    if (conflicts.length) {
      return {
        ...refuse('stale', `${conflicts.length} object(s) changed since the grant was minted`),
        conflicts,
      };
    }

    const objects = new Map<ObjectId, ObjectRecord | null>();
    for (const [id, slots] of working) {
      const prior = priors.get(id)!;
      objects.set(id, { ...prior, revision: asRevision(prior.revision + 1), slots });
    }

    // Every relation instruction was an idempotent no-op, and no value was
    // written either. 0016: *"A successful idempotent no-op produces no undo
    // entry"* — so this is a success with an empty journal rather than a commit
    // whose undo would delete a relation nobody created.
    if (objects.size === 0 && edgeEdits.size === 0) {
      const empty = this.emptyCommit('binding', a.specHash);
      return empty.ok ? { ...empty, membership: { linked, unlinked } } : empty;
    }

    const committed = await this.applyCommit({
      via: 'binding',
      basis,
      ...opt('specHash', a.specHash),
      objects,
      keys: new Map(),
      edges: edgeEdits,
      steps: expressible && steps.length === valued.length ? steps : null,
      restore: {
        objects: [...priors.values()].map(toPersistedObject),
        absentObjects: [], keys: [], absentKeys: [],
        // What `undo` puts back: an edge this commit REMOVED comes back whole,
        // and one it ADDED goes away again.
        edges: restoredEdges.map(toPersistedEdge),
        absentEdges: absentEdges as string[],
        scope: [...wrote].map(([id, keys]) => ({ id: id as string, keys: [...keys] as string[] })),
      },
      changedRoles,
      // An edge changes who is in a relation, not what an object's field says.
      kind: edgeEdits.size > 0 && objects.size === 0 ? 'membership' : 'values',
    });
    return committed.ok && relational.length > 0
      ? { ...committed, membership: { linked, unlinked } }
      : committed;
  }

  /* ─────────────────────────────────────────────────────────────────  edit */

  async edit(
    a: EditorAuthority, basis: number, fn: (s: EditSession) => Promise<void> | void,
    manifest?: ManifestRef,
  ): Promise<CommitResult> {
    if (!isEditorAuthority(a)) return refuse('not-editable', FORGED);
    if (this.closed) return refuse('closed', 'the store is closed');
    const at = this.now();
    const ops: EditOp[] = [];

    const session: EditSession = {
      reader: this,
      create: (d) => {
        const id = d.id ?? newObjectId();
        ops.push({ t: 'create', id, ...opt('typeHint', d.typeHint),
          properties: [...(d.properties ?? [])] });
        return id;
      },
      defineProperty: (d) => {
        const id = newPropertyKeyId();
        ops.push({ t: 'define', rec: {
          id, label: d.label, kind: d.kind, ...opt('roleHint', d.roleHint), createdAt: at,
        } });
        return id;
      },
      set: (o, key, v) => { ops.push({ t: 'set', o, key, v }); },
      clear: (o, key) => { ops.push({ t: 'clear', o, key }); },
      drop: (o, key) => { ops.push({ t: 'drop', o, key }); },
      link: (from, key, to, anchor) => {
        const id = newEdgeId();
        // The role is READ OFF THE KEY, never taken from the caller. A caller
        // that could name both would be able to file an edge under a role its
        // key does not have, and every later read would believe it.
        // Pending ops first: `defineProperty` and `link` in ONE session is the
        // ordinary shape — the demo's seed does exactly that — and the key
        // registry does not have the record until the commit applies.
        const pending = ops.find(
          (o): o is Extract<EditOp, { t: 'define' }> => o.t === 'define' && o.rec.id === key);
        const via = (pending?.rec ?? this.keys.get(key))?.roleHint;
        ops.push({ t: 'link',
          edge: { id, from, key, via: via ?? ('relation.peer' as Role), to,
                  ...opt('anchor', anchor), at },
          // Carried so `apply` can refuse with the real reason rather than
          // inventing a role above and then validating the invention.
          keyed: via !== undefined });
        return id;
      },
      unlink: (edge) => { ops.push({ t: 'unlink', edge }); },
      retire: (o) => { ops.push({ t: 'retire', o }); },
      bindRole: (key, role) => { ops.push({ t: 'bindRole', key, role }); },
      publishRole: (role, key) => {
        if (manifest === undefined) {
          // A programming error, not a silent no-op: publishing into no
          // manifest is a caller believing a role became reachable when
          // nothing was extended, which is the exact state `0126` exists to
          // remove.
          throw new TypeError(
            'publishRole needs a base manifest — pass one as edit()\u2019s fourth argument');
        }
        ops.push({ t: 'manifest', role, key });
      },
    };

    await fn(session);
    if (ops.length === 0) return this.emptyCommit('editor');

    const objects = new Map<ObjectId, ObjectRecord>();
    const priors = new Map<ObjectId, ObjectRecord>();
    /** Per object, what this commit touched — the same scoping the binding door
     *  records, built from the op list rather than from the grants. */
    const wrote = new Map<ObjectId, { keys: Set<PropertyKeyId>; lifecycle: boolean }>();
    const scopeOf = (id: ObjectId) => {
      let e = wrote.get(id);
      if (!e) { e = { keys: new Set(), lifecycle: false }; wrote.set(id, e); }
      return e;
    };
    const created = new Set<ObjectId>();
    const keys = new Map<PropertyKeyId, PropertyKeyRecord>();
    const priorKeys = new Map<PropertyKeyId, PropertyKeyRecord>();
    const definedKeys = new Set<PropertyKeyId>();
    const edges = new Map<EdgeId, EdgeRecord | null>();
    const removedEdges: EdgeRecord[] = [];
    const addedEdges = new Set<EdgeId>();
    const changedRoles = new Set<Role>();
    /**
     * Objects whose RELATION SET this commit changes.
     *
     * The policy check below reads `objects`, which holds what the value
     * instructions touched. An edge touches neither endpoint's record, so an
     * actor allowed to create but not to edit a protected document could
     * `create` a fresh block and `link` it onto that document, and the check
     * saw nothing to check. Measured: `ok: true`, and the document went from
     * one root to two. The mirror gap let an actor who may edit a child unlink
     * it from a parent they may not touch.
     *
     * The SOURCE is what is authorized, because the source is what owns the
     * set. Being referenced is not being edited: an embed pointing at a note
     * changes the embed's outgoing set, not the note.
     */
    const relinked = new Set<ObjectId>();
    let kind: StoreEvent['kind'] = 'values';

    // Keys first, whatever order the builder ran in. `defineProperty` and the
    // `set` that fills the new property are one commit by design, and a caller
    // should not have to know which line the store looks at first.
    for (const op of ops) {
      if (op.t !== 'define') continue;
      keys.set(op.rec.id, op.rec);
      definedKeys.add(op.rec.id);
      kind = 'schema';
    }

    const keyOf = (id: PropertyKeyId) => keys.get(id) ?? this.keys.get(id);
    const recOf = (id: ObjectId) => objects.get(id) ?? this.objects.get(id);
    const touch = (id: ObjectId): ObjectRecord | undefined => {
      const existing = objects.get(id);
      if (existing) return existing;
      const committed = this.objects.get(id);
      if (!committed) return undefined;
      priors.set(id, committed);
      const next = { ...committed, slots: new Map(committed.slots) };
      objects.set(id, next);
      return next;
    };

    for (const op of ops) {
      switch (op.t) {
        case 'create': {
          if (this.objects.has(op.id)) {
            return refuse('unknown-object', `object "${op.id}" already exists`);
          }
          const slots = new Map<PropertyKeyId, PropertySlot>();
          for (const [key, v] of op.properties) {
            const keyRec = keyOf(key);
            if (!keyRec) return refuse('unknown-slot', `property key "${key}" does not exist`);
            if (!valueFitsKind(v, keyRec.kind)) {
              return refuse('kind-mismatch',
                `a ${kindOf(v) ?? 'unknown'} value does not fit a ${keyRec.kind} property`);
            }
            slots.set(key, { state: 'set', value: v, updatedAt: at });
          }
          created.add(op.id);
          objects.set(op.id, {
            id: op.id, revision: asRevision(1), ...opt('typeHint', op.typeHint), slots,
          });
          kind = 'membership';
          break;
        }
        case 'define':
          // The absence of a migration, in one line: a new key is appended to
          // the registry and no existing object is rewritten. Objects without
          // it are `absent`, which is a different state from `empty` — and only
          // that distinction tells a column with holes from no column at all.
          break;
        case 'set': {
          const rec = touch(op.o);
          if (!rec) return refuse('unknown-object', `object "${op.o}" does not exist`);
          const keyRec = keyOf(op.key);
          if (!keyRec) return refuse('unknown-slot', `property key "${op.key}" does not exist`);
          if (!valueFitsKind(op.v, keyRec.kind)) {
            return refuse('kind-mismatch',
              `a ${kindOf(op.v) ?? 'unknown'} value does not fit a ${keyRec.kind} property`);
          }
          (rec.slots as Map<PropertyKeyId, PropertySlot>)
            .set(op.key, { state: 'set', value: op.v, updatedAt: at });
          scopeOf(op.o).keys.add(op.key);
          break;
        }
        case 'clear': {
          const rec = touch(op.o);
          if (!rec) return refuse('unknown-object', `object "${op.o}" does not exist`);
          (rec.slots as Map<PropertyKeyId, PropertySlot>)
            .set(op.key, { state: 'empty', updatedAt: at });
          scopeOf(op.o).keys.add(op.key);
          break;
        }
        case 'drop': {
          const rec = touch(op.o);
          if (!rec) return refuse('unknown-object', `object "${op.o}" does not exist`);
          (rec.slots as Map<PropertyKeyId, PropertySlot>).delete(op.key);
          scopeOf(op.o).keys.add(op.key);
          break;
        }
        case 'link': {
          const keyRec = op.edge.key !== undefined ? keyOf(op.edge.key) : undefined;
          if (!op.keyed || !keyRec) {
            return refuse('unknown-slot',
              `property key "${String(op.edge.key)}" does not exist, or names no role`);
          }
          if (ROLE_KIND[op.edge.via] !== 'ref') {
            return refuse('kind-mismatch',
              `key "${String(op.edge.key)}" names role "${op.edge.via}", which is `
              + `${ROLE_KIND[op.edge.via]} rather than a ref`);
          }
          if (!recOf(op.edge.from) || !recOf(op.edge.to)) {
            return refuse('unknown-object', 'a link needs both endpoints to exist');
          }
          edges.set(op.edge.id, op.edge);
          addedEdges.add(op.edge.id);
          relinked.add(op.edge.from);
          changedRoles.add(op.edge.via);
          // An edge can MOVE a traversal scope's membership, so a commit that
          // writes one is a membership change and not only a value change.
          if (kind === 'values') kind = 'membership';
          break;
        }
        case 'unlink': {
          const existing = this.edges.get(op.edge);
          if (!existing) break;
          edges.set(op.edge, null);
          removedEdges.push(existing);
          relinked.add(existing.from);
          changedRoles.add(existing.via);
          if (kind === 'values') kind = 'membership';
          break;
        }
        case 'retire': {
          const rec = touch(op.o);
          if (!rec) return refuse('unknown-object', `object "${op.o}" does not exist`);
          objects.set(op.o, { ...rec, deletedAt: at });
          scopeOf(op.o).lifecycle = true;
          kind = 'membership';
          break;
        }
        case 'bindRole': {
          const keyRec = keyOf(op.key);
          if (!keyRec) return refuse('unknown-slot', `property key "${op.key}" does not exist`);
          if (!priorKeys.has(op.key) && !definedKeys.has(op.key)) priorKeys.set(op.key, keyRec);
          keys.set(op.key, { ...keyRec, roleHint: op.role });
          changedRoles.add(op.role);
          kind = 'schema';
          break;
        }
      }
    }

    if (this.policy) {
      for (const id of new Set([...objects.keys(), ...relinked])) {
        if (created.has(id)) continue;
        if (!(await this.policy.canEdit(a.actor, id))) {
          return refuse('not-editable', `actor "${a.actor.id}" may not edit object "${id}"`);
        }
      }
    }

    const finalObjects = new Map<ObjectId, ObjectRecord | null>();
    for (const [id, rec] of objects) {
      const prior = priors.get(id);
      finalObjects.set(id, prior
        ? { ...rec, revision: asRevision(prior.revision + 1) }
        : rec);
    }
    for (const rec of finalObjects.values()) {
      if (!rec) continue;
      for (const [key] of rec.slots) {
        const role = keyOf(key)?.roleHint;
        if (role) changedRoles.add(role);
      }
    }

    /**
     * The manifest the published roles produce, built BEFORE the commit so its
     * vocabulary check can refuse the whole edit rather than half of it.
     *
     * `manifestRow` is the same helper `storeManifest` uses, which is the point:
     * a second copy of the closed-vocabulary check is the drift decision `0044`
     * §1 names, one package out. A role outside the set throws here, before any
     * value is written.
     *
     * The base is read from the engine's index rather than taken from the
     * caller, so a caller cannot publish into a manifest this store has never
     * seen. `edit()` refuses a `publishRole` with no base at all.
     */
    const published = ops.filter(
      (o): o is Extract<EditOp, { t: 'manifest' }> => o.t === 'manifest');
    let manifestRow: { ref: ManifestRef; deduped: readonly ManifestEntry[] } | undefined;
    if (published.length > 0) {
      const prior = this.manifests.get(manifest!);
      if (!prior) throw new Error(`manifest "${manifest}" is not known to this store`);
      /**
       * The base is the freshest KNOWN EXTENSION of the caller's manifest, not
       * the caller's ref itself — @Codex, P1 on `18eae3f`. Two tabs, one pool:
       * A publishes a role (the manifest moves), B's write is refused stale,
       * the engine re-hydrates — so this map now HOLDS A's tip — and B's
       * retry still names the ref it planned against, because the overlay B
       * rebases from is written a beat after A's commit. Extending the stale
       * ref forked the lineage: B's manifest carried only B's role, and A's
       * durable attachment stopped resolving for every scope that adopted it.
       *
       * The union of every superset tip, rather than a pick between them:
       * entries are (role, key) pairs deduped by pair, a role may legally
       * carry several keys, and a union is the one result that orphans
       * neither side — it also heals a fork that already happened, the next
       * time either side publishes. When no extension exists this is exactly
       * `prior.entries`, and the content-addressed ref dedups to the same
       * manifest as before.
       */
      const sigOf = (e: ManifestEntry): string => `${e.role} ${e.key}`;
      const baseSigs = new Set(prior.entries.map(sigOf));
      const union: ManifestEntry[] = [...prior.entries];
      const inUnion = new Set(baseSigs);
      for (const held of this.manifests.values()) {
        if (held.entries.length <= prior.entries.length) continue;
        const sigs = held.entries.map(sigOf);
        if (![...baseSigs].every((sig) => sigs.includes(sig))) continue;
        for (const e of held.entries) {
          if (inUnion.has(sigOf(e))) continue;
          inUnion.add(sigOf(e));
          union.push(e);
        }
      }
      /**
       * The KEY, checked the way `set`, `link` and `bindRole` check theirs.
       *
       * @Codex, P2 on #184: *"When `publishRole()` is given a stale key or a
       * `PropertyKeyId` obtained from another store, this copies it directly
       * into the new manifest without the `keyOf()` validation applied to
       * `set`, `link`, and `bindRole`. The commit therefore succeeds and
       * returns a manifest that claims the role was published even though no
       * property in this store can resolve through that key."*
       *
       * Correct, and it is my own gap from `0126` rather than an old one:
       * `git log -S` puts the unchecked spread in `c92eb88`, the commit that
       * added `publishRole`. The role-vocabulary check was carried over from
       * `storeManifest` and the key check was not, because `storeManifest`
       * never had one to carry — it takes entries a caller assembled, while
       * this path mints them inside an edit that already knows which keys are
       * real.
       *
       * `keyOf` reads pending definitions first and committed ones second, so a
       * key defined in THIS edit still publishes — which is the ordinary case,
       * and the reason the check cannot simply consult `this.keys`.
       *
       * It refuses here rather than at `applyCommit` for the reason the
       * comment above already gives: the manifest is built before the commit so
       * a bad row refuses the whole edit rather than half of it.
       */
      for (const o of published) {
        if (!keyOf(o.key)) {
          return refuse('unknown-slot',
            `property key "${o.key}" does not exist, so publishing role `
            + `"${o.role}" would claim a reachability this store cannot resolve`);
        }
      }
      manifestRow = this.manifestRow([
        ...union,
        ...published.map((o) => ({ role: o.role, key: o.key })),
      ]);
    }

    return this.applyCommit({
      via: 'editor',
      basis,
      objects: finalObjects,
      keys: new Map(keys),
      edges,
      steps: null,
      ...(manifestRow ? { manifest: manifestRow } : {}),
      restore: {
        objects: [...priors.values()].map(toPersistedObject),
        absentObjects: [...created],
        keys: [...priorKeys.values()].map(toPersistedKey),
        absentKeys: [...definedKeys],
        // The manifest this edit was made AGAINST, recorded only when the edit
        // published a role — otherwise there is nothing to put back. Undo of a
        // publish that also minted its key would otherwise delete the key and
        // leave the scope on a manifest naming it. @Codex, P2 on `ef26359`.
        ...(manifestRow && manifest ? { manifest: manifest as string } : {}),
        edges: removedEdges.map(toPersistedEdge),
        absentEdges: [...addedEdges],
        // Created objects are restored by DELETING them, so they need no key
        // scope; only the objects that already existed do.
        scope: [...wrote].filter(([id]) => priors.has(id)).map(([id, e]) => ({
          id: id as string, keys: [...e.keys] as string[], ...(e.lifecycle ? { lifecycle: true } : {}),
        })),
      },
      changedRoles,
      kind,
    });
  }

  /**
   * One undo stack across both doors (0007).
   *
   * It restores a snapshot rather than replaying inverse operations, because
   * the mutation vocabulary cannot express "make this slot absent again" and an
   * operation log that silently leaves the slot `empty` is a lossy undo that
   * looks like a working one.
   *
   * Two things it does NOT do, both of which it used to:
   *
   *  - **It does not put the whole object back.** Only the slots the undone
   *    commit wrote are restored; everything else keeps its current value. The
   *    old behaviour reverted every later write to the same object, so undoing
   *    a country edit deleted a note added after it — data loss nobody asked
   *    for, reachable from the demo's Ctrl+Z.
   *  - **It does not put the old revision back.** An undo is a new fact about
   *    the object and commits FORWARD like any other write. Restoring the old
   *    counter made revisions non-monotonic, which defeats the optimistic check
   *    in `write` — a grant minted before the undo matched again afterwards and
   *    a stale write landed.
   */
  async undo(a: EditorAuthority, basis: number, entry: JournalId): Promise<CommitResult> {
    if (!isEditorAuthority(a)) return refuse('not-editable', FORGED);
    if (this.closed) return refuse('closed', 'the store is closed');
    const rec = this.journal.get(entry);
    if (!rec) return refuse('unknown-object', `journal entry "${entry}" does not exist`);

    const scoped = new Map<string, RestoreScope>();
    for (const sc of rec.restore.scope ?? []) scoped.set(sc.id, sc);

    const objects = new Map<ObjectId, ObjectRecord | null>();
    const priors: PersistedObject[] = [];
    const reCreated: ObjectId[] = [];
    /** What this undo itself changes, so redo — an undo of this commit — is
     *  scoped the same way rather than falling back to a whole-object restore. */
    const scope: RestoreScope[] = [];

    for (const o of rec.restore.objects) {
      const id = o.id as ObjectId;
      const before = fromPersistedObject(o);
      const current = this.objects.get(id);
      if (!current) {
        // Nothing to merge into: the object is gone, so the snapshot comes back
        // whole, one revision past where it left off.
        // No scope entry: this undo's own inverse is "delete it again", which
        // `reCreated` records. A key list here would never be read.
        reCreated.push(id);
        objects.set(id, { ...before, revision: asRevision(before.revision + 1) });
        continue;
      }
      priors.push(toPersistedObject(current));
      const sc = scoped.get(id as string);
      const keys = sc
        ? sc.keys
        // A journal written before undo was scoped. Whole-object restore is what
        // it recorded and all it can support — including the data loss — but the
        // revision still moves forward.
        : [...new Set([...before.slots.keys(), ...current.slots.keys()])] as string[];
      const lifecycle = sc ? sc.lifecycle === true : true;

      const slots = new Map(current.slots);
      for (const k of keys) {
        const prior = before.slots.get(k as PropertyKeyId);
        if (prior) slots.set(k as PropertyKeyId, prior);
        else slots.delete(k as PropertyKeyId);      // the commit created this slot
      }
      objects.set(id, {
        id,
        revision: asRevision(current.revision + 1),
        ...opt('typeHint', current.typeHint),
        slots,
        ...opt('deletedAt', lifecycle ? before.deletedAt : current.deletedAt),
      });
      scope.push({ id: id as string, keys, ...(lifecycle ? { lifecycle: true } : {}) });
    }

    for (const id of rec.restore.absentObjects) {
      const current = this.objects.get(id as ObjectId);
      if (current) {
        priors.push(toPersistedObject(current));
        scope.push({
          id, keys: [...current.slots.keys()] as string[], lifecycle: true,
        });
      }
      objects.set(id as ObjectId, null);
    }

    const keys = new Map<PropertyKeyId, PropertyKeyRecord | null>();
    for (const k of rec.restore.keys) keys.set(k.id as PropertyKeyId, fromPersistedKey(k));
    for (const id of rec.restore.absentKeys) keys.set(id as PropertyKeyId, null);

    const edges = new Map<EdgeId, EdgeRecord | null>();
    for (const e of rec.restore.edges) edges.set(e.id as EdgeId, fromPersistedEdge(e));
    for (const id of rec.restore.absentEdges) edges.set(id as EdgeId, null);

    if (this.policy) {
      for (const id of objects.keys()) {
        if (!this.objects.has(id)) continue;
        if (!(await this.policy.canEdit(a.actor, id))) {
          return refuse('not-editable', `actor "${a.actor.id}" may not edit object "${id}"`);
        }
      }
    }

    /**
     * The manifest the undone edit was made against, put back with everything
     * else it touched.
     *
     * Re-indexed through `applyCommit`'s ordinary manifest path rather than by
     * writing `this.manifests` here, so it lands after the backend accepts the
     * patch — the rule `0126` already states about a ref readable in this
     * engine and nowhere else. It also makes the commit REPORT the ref, which
     * is how a caller learns to move its scope back; a restored manifest the
     * caller never hears about is the same half-revert in a different place.
     *
     * Unknown refs are skipped rather than refused: a journal can outlive the
     * manifest it names, and failing an undo because of that would strand the
     * whole record for one field.
     */
    const priorManifest = rec.restore.manifest
      ? this.manifests.get(rec.restore.manifest as ManifestRef) : undefined;

    return this.applyCommit({
      via: 'editor',
      basis,
      objects, keys, edges,
      steps: null,
      ...(priorManifest
        ? { manifest: { ref: priorManifest.ref, deduped: [...priorManifest.entries] } }
        : {}),
      restore: {
        /**
         * The manifest this UNDO replaces, so a redo can put it back.
         *
         * @Codex, P2 on `250d2f9`: *"When a role-publishing edit is undone,
         * this branch restores the base manifest, but the journal created for
         * that undo records only the object, key, and edge inverses. Redo is
         * implemented by undoing this new journal, so it restores the published
         * key and media value without restoring or reporting the published
         * manifest; the role remains unreachable through the base manifest even
         * though the UI reports a successful redo."*
         *
         * Right, and it is the asymmetry showing up one level out: the original
         * edit records BOTH ends of its manifest swap — `manifest` for what it
         * published and `restore.manifest` for what it replaced — and the undo
         * recorded only the half it was reversing. Undo is a commit like any
         * other and owes the same record.
         *
         * `rec.publishedManifest` rather than anything reconstructed: it is the
         * ref this record's own commit published, written down at the time. A
         * record from before that field existed leaves this absent and redoes
         * exactly as it did before, which is a fact this cannot invent.
         */
        ...(rec.publishedManifest ? { manifest: rec.publishedManifest } : {}),
        objects: priors,
        absentObjects: reCreated,
        keys: [...keys.keys()]
          .map((k) => this.keys.get(k)).filter((k): k is PropertyKeyRecord => !!k)
          .map(toPersistedKey),
        absentKeys: [],
        edges: [...edges.keys()]
          .map((e) => this.edges.get(e)).filter((e): e is EdgeRecord => !!e).map(toPersistedEdge),
        absentEdges: [],
        scope,
      },
      changedRoles: new Set(),
      kind: 'membership',
    });
  }

  async close(): Promise<void> {
    this.closed = true;
    this.listeners.clear();
    await this.persistence.close();
  }

  /* ─────────────────────────────────────────────────────────────  internals */

  private manifestOf(ref: ManifestRef): ManifestIndex {
    const mi = this.manifests.get(ref);
    if (!mi) throw new Error(`manifest "${ref}" is not known to this store`);
    return mi;
  }

  /**
   * `traversalMembers` is graph-defined membership, computed ONCE by the caller
   * and consulted here by identity.
   *
   * @Codex's ruling on task 0083: *"the implementation should test the record
   * id against the computed scope membership, rather than teaching the
   * per-record predicate a graph walk."* A traversal is not a per-object
   * question — whether an object is in depends on the other objects — so this
   * predicate keeps its shape and the walk happens above it.
   */
  private inScope(
    rec: ObjectRecord, scope: Scope, mi: ManifestIndex,
    traversalMembers?: ReadonlySet<ObjectId>,
  ): boolean {
    if (rec.deletedAt) return false;
    if (scope.typeHint !== undefined && rec.typeHint !== scope.typeHint) return false;
    // The traversal's closure REPLACES the seed test: the seed is where the
    // walk started, not the membership it defines.
    if (traversalMembers) {
      if (!traversalMembers.has(rec.id)) return false;
    } else if (scope.seed && !scope.seed.includes(rec.id)) return false;
    const filter = scope.query?.filter;
    // A value-taking predicate whose view-state slot is unset is absent, not
    // false.  This distinction matters inside `any` and `not`: pretending an
    // untouched control is a failed comparison can empty a lens before the
    // person has asked it to filter anything.
    const filtered = filter ? this.evalFilter(filter, rec, mi, scope.viewState ?? []) : undefined;
    return filtered !== false;
  }

  /**
   * The whole collection in id order, sorted lazily on the first membership
   * read after one or more writes rather than on every read.
   *
   * Task 0039 measured `count`, `query` and `projection` landing within a
   * millisecond of each other at 100k, because each one materialized every
   * record and `localeCompare`-sorted it before taking the page anybody asked
   * for. Of the ~10ms that cost, the materialize-and-sort was ~7ms — and a
   * render performs three membership reads with no write between them, so it
   * paid for the same sort three times.
   *
   * The task's first question was whether the sort is redundant outright:
   * `this.objects` is a `Map`, ids are UUIDv7, so insertion order should
   * already BE id order. Measured over 100,000 creates, it is — and the obvious
   * counter-example does not work either. `undo` of a create is the one path
   * that calls `this.objects.delete`, but ids only increase, so the hole it
   * leaves is always at the end and anything created after it sorts last
   * anyway. `membership-memo.test.ts` now inserts explicit ids in descending order, so
   * deleting this sort fails for the read-contract reason the guard names.
   *
   * Two reasons it stays regardless, neither of them demonstrated here:
   *
   *  - `hydrate` inserts in whatever order the persistence layer hands back.
   *    IndexedDB keys by id and so almost certainly returns id order, but
   *    "almost certainly" about another component's iteration order is not a
   *    thing to make the read path depend on.
   *  - The UUIDv7 counter is 12 bits. More than 4,096 ids minted inside one
   *    millisecond wrap it and go backwards. At the 14µs-per-object creation
   *    rate measured here the worst burst seen was 375, so the margin is real —
   *    but it is a margin, not a guarantee, and it shrinks on faster hardware.
   *
   * So the sort stays and the REPEAT of it goes. That is the whole change: it
   * costs at most one sort on the first read after one or more writes instead
   * of one sort per membership read, and it does not require
   * anybody to be right about the two things above.
   *
   * Deliberately not also swapping `localeCompare` for `<`, which measured 25%
   * faster on these ids: that is a second change, and this one is easier to
   * review alone.
   */
  private baseInIdOrder(): readonly ObjectRecord[] {
    if (this.sortedBase) return this.sortedBase;
    const base = [...this.objects.values()].sort((a, b) => a.id.localeCompare(b.id));
    this.sortedBase = base;
    return base;
  }

  /** Membership, in a deterministic order. Id order when nothing else is asked
   *  for — UUIDv7 sorts by creation, so a capped shape sample is the same
   *  sample on the next render and a role near the coverage floor cannot
   *  flicker. */
  private members(scope: Scope): readonly ObjectRecord[] {
    const key = scopeKey(scope);
    const memo = this.membership.get(key);
    if (memo) return memo;

    const mi = this.manifestOf(scope.manifest);
    const traversalMembers = this.traversalMembers(scope, mi);
    // `filter` copies, so `sortedBase` is never handed to a caller and cannot
    // be sorted out from under itself by `scope.query.sort` below.
    const base = traversalMembers
      ? [...traversalMembers].map((id) => this.objects.get(id))
        .filter((r): r is ObjectRecord => !!r)
      : scope.seed
        ? scope.seed.map((id) => this.objects.get(id)).filter((r): r is ObjectRecord => !!r)
        : this.baseInIdOrder();
    const kept = base.filter((r) => this.inScope(r, scope, mi, traversalMembers));
    const sort = scope.query?.sort;
    if (sort?.length) {
      kept.sort((a, b) => {
        for (const s of sort) {
          const c = compareRaw(this.firstRaw(a, mi, s.role), this.firstRaw(b, mi, s.role));
          if (c !== 0) return s.direction === 'asc' ? c : -c;
        }
        return a.id.localeCompare(b.id);
      });
    }
    if (this.membership.size >= StoreEngine.MEMBERSHIP_CACHE_MAX) this.membership.clear();
    this.membership.set(key, kept);
    return kept;
  }

  /**
   * Graph-defined membership, computed once.
   *
   * `query.traverse` sat in the layout schema with a validation rule of its
   * own, was projected into the pooled genome, and was read by NOTHING — the
   * store's `query()` looked at `filter` and `sort` alone, so a seeded scope
   * returned the same one object with a traversal declared as without, while
   * `store.traverse()` reached further from that same seed. Task 0083 measured
   * it; @Codex ruled that the field defines membership rather than describing
   * private input to the force-graph capability: *"a capability receives the
   * RESULT of that query; it must not privately reinterpret an otherwise inert
   * query field."*
   *
   * OUTWARD, like following a lookup from its subject, and the live seeds are
   * always members — a walk that dropped its own starting points would make a
   * depth-1 traversal smaller than no traversal at all.
   */
  private traversalMembers(scope: Scope, mi: ManifestIndex): ReadonlySet<ObjectId> | undefined {
    const traversal = scope.query?.traverse;
    if (!traversal || !scope.seed) return undefined;

    const members = new Set<ObjectId>();
    const queue: { id: ObjectId; depth: number }[] = scope.seed.map((id) => ({ id, depth: 0 }));
    for (let i = 0; i < queue.length; i += 1) {
      const current = queue[i]!;
      if (members.has(current.id)) continue;
      const rec = this.objects.get(current.id);
      if (!rec || rec.deletedAt) continue;
      members.add(current.id);
      if (current.depth >= traversal.depth) continue;
      for (const edge of this.neighbours(current.id, traversal.via, 'out', mi)) {
        if (!members.has(edge.to)) queue.push({ id: edge.to, depth: current.depth + 1 });
      }
    }
    return members;
  }

  private valuesForRole(rec: ObjectRecord, mi: ManifestIndex, role: Role): Value[] {
    const out: Value[] = [];
    for (const k of mi.keysByRole.get(role) ?? []) {
      const s = rec.slots.get(k);
      if (s?.state === 'set' && isPopulated(s.value)) out.push(s.value);
    }
    return out;
  }

  private firstRaw(rec: ObjectRecord, mi: ManifestIndex, role: Role): unknown {
    const v = this.valuesForRole(rec, mi, role)[0];
    return v === undefined ? undefined : rawOf(v);
  }

  private evalFilter(
    node: FilterNode, rec: ObjectRecord, mi: ManifestIndex, viewState: readonly Value[],
  ): boolean | undefined {
    if ('all' in node) {
      const active = node.all.map((c) => this.evalFilter(c, rec, mi, viewState))
        .filter((v): v is boolean => v !== undefined);
      return active.length ? active.every(Boolean) : undefined;
    }
    if ('any' in node) {
      const active = node.any.map((c) => this.evalFilter(c, rec, mi, viewState))
        .filter((v): v is boolean => v !== undefined);
      return active.length ? active.some(Boolean) : undefined;
    }
    if ('not' in node) {
      const active = this.evalFilter(node.not, rec, mi, viewState);
      return active === undefined ? undefined : !active;
    }
    const w = node.where;
    const arg = w.valueFrom === undefined ? undefined : viewState[w.valueFrom];
    // Operators without an argument remain active. A value-taking predicate is
    // inactive until its declared control has a value.
    if (w.valueFrom !== undefined && arg === undefined
      && w.op !== 'is-empty' && w.op !== 'is-not-empty') return undefined;
    return testPredicate(this.valuesForRole(rec, mi, w.role), w.op, arg);
  }

  private materialize(rec: ObjectRecord, mi: ManifestIndex): StoredObject {
    const roles = new Map<Role, readonly Value[]>();
    for (const [role] of mi.keysByRole) {
      const vals = this.valuesForRole(rec, mi, role);
      if (vals.length) roles.set(role, vals);
    }
    return {
      id: rec.id,
      revision: rec.revision,
      ...opt('typeHint', rec.typeHint),
      slots: rec.slots,
      roles,
      ...opt('deletedAt', rec.deletedAt),
    };
  }

  /** The lens a scope reads through. Unknown manifests in `across` are dropped
   *  rather than thrown on: a saved scope outlives the pool it named, and a
   *  reference that stops resolving is a worse failure than one that never
   *  did. */
  private lensFor(scope: Scope): Lens {
    const across: ManifestIndex[] = [];
    for (const ref of scope.across ?? []) {
      const m = this.manifests.get(ref);
      if (m && m.ref !== scope.manifest) across.push(m);
    }
    const mi = this.manifestOf(scope.manifest);
    return { mi, across, reachable: [mi, ...across] };
  }

  /** Whether a manifest names any key this object actually has set — "is this
   *  the document that object lives in". Not identity: nothing records an
   *  object's manifest (see `test/manifest-boundary.test.ts`), so addressing
   *  it is the only question the store can answer. */
  private addresses(rec: ObjectRecord, mi: ManifestIndex): boolean {
    for (const [k, slot] of rec.slots) {
      if (slot.state === 'set' && mi.roleByKey.has(k)) return true;
    }
    return false;
  }

  private projectOne(
    rec: ObjectRecord, lens: Lens, depth: number, path: ReadonlySet<ObjectId>,
  ): RoleValues {
    const out: Record<string, unknown> = {};
    for (const [role, keys] of lens.mi.keysByRole) {
      const vals: unknown[] = [];
      for (const k of keys) {
        const s = rec.slots.get(k);
        if (s?.state !== 'set' || !isPopulated(s.value)) continue;
        vals.push(this.expand(s.value, lens, depth, path));
      }
      // An explicit edge is the same relation as a ref-valued slot. Projecting
      // only one of them would make what a lens renders depend on how the
      // relation happened to be recorded.
      if (ROLE_KIND[role] === 'ref') {
        for (const eid of this.outEdges.get(rec.id) ?? []) {
          const e = this.edges.get(eid);
          if (e && edgeIsUnder(e, role, keys)) {
            vals.push(this.expand({ k: 'ref', v: e.to }, lens, depth, path));
          }
        }
      }
      if (vals.length === 1) out[role] = vals[0];
      else if (vals.length > 1) out[role] = vals;
    }
    return out;
  }

  private expand(
    v: Value, lens: Lens, depth: number, path: ReadonlySet<ObjectId>,
  ): unknown {
    if (v.k === 'ref' && depth > 0) {
      const target = this.objects.get(v.v);
      // Path-based, not visited-set-based — the same stop condition `node-slot`
      // uses, and for the same reason: two siblings pointing at one object are
      // not a cycle, and refusing them would break ordinary shared references.
      if (!target || target.deletedAt || path.has(v.v)) return v.v;

      // THE CROSS-DOC REFERENCE. A same-pool target reads through the scope's
      // own manifest, exactly as before — that check comes first so nothing
      // declared in `across` can shadow the pool a lens is actually looking at.
      // A target the reading manifest cannot address reads through the first
      // DECLARED manifest that can, in the order the caller listed them.
      //
      // Falls back to the reading manifest when nothing addresses the target,
      // which is the unchanged behaviour: an empty projection. A reference out
      // of the pool that nobody declared still resolves to nothing.
      const through = this.addresses(target, lens.mi)
        ? lens.mi
        : lens.reachable.find((m) => this.addresses(target, m)) ?? lens.mi;
      const projected = this.projectOne(
        target, { ...lens, mi: through }, depth - 1, new Set([...path, v.v]));
      // THE OCCURRENCE, named. Decision 0016 asks projection to mint a handle
      // for every rendered relation occurrence, and this is the only place a
      // relation becomes something on a card. The token travels as a string
      // under a symbol, so it survives a DOM attribute and stays invisible to
      // every consumer that treats own keys as roles.
      //
      // Only for a target this lens can actually READ. A reference nobody
      // declared already resolves to nothing — task 0050's boundary — and a
      // handle minted for it would be a write capability for an object outside
      // the document, handed out through the one call that is supposed to
      // return nothing. Found immediately: `cross-doc.test.ts` asserts that
      // projection deeply equals `{}` there, and a symbol-only object is not
      // `{}`.
      return this.addresses(target, through)
        ? { ...projected, [RELATION_TARGET_KEY]: this.mintTarget(v.v, v.anchor).token }
        : projected;
    }
    if (v.k === 'list') return v.v.map((x) => this.expand(x, lens, depth, path));
    return rawOf(v);
  }

  private neighbours(
    id: ObjectId, via: Role, direction: Traversal['direction'], mi: ManifestIndex,
  ): TraversalEdge[] {
    const out: TraversalEdge[] = [];
    const keys = mi.keysByRole.get(via) ?? [];
    if (direction !== 'in') {
      const rec = this.objects.get(id);
      for (const k of keys) {
        const s = rec?.slots.get(k);
        if (s?.state === 'set' && s.value.k === 'ref') {
          out.push({ from: id, via, to: s.value.v, ...opt('anchor', s.value.anchor),
                     target: this.mintTarget(s.value.v, s.value.anchor) });
        }
      }
      for (const eid of this.outEdges.get(id) ?? []) {
        const e = this.edges.get(eid);
        if (e && edgeIsUnder(e, via, keys)) {
          out.push({ from: e.from, via, to: e.to, ...opt('anchor', e.anchor),
                     target: this.mintTarget(e.to, e.anchor) });
        }
      }
    }
    if (direction !== 'out') {
      for (const eid of this.inEdges.get(id) ?? []) {
        const e = this.edges.get(eid);
        if (e && edgeIsUnder(e, via, keys)) {
          out.push({ from: e.from, via, to: e.to, ...opt('anchor', e.anchor),
                     target: this.mintTarget(e.to, e.anchor) });
        }
      }
      // Ref-valued slots have no reverse index yet, so this is a scan. Bounded
      // by `maxNodes`, and the honest place to fix it is an index, not a
      // narrower traversal.
      for (const rec of this.objects.values()) {
        if (rec.deletedAt) continue;
        for (const k of keys) {
          const s = rec.slots.get(k);
          if (s?.state === 'set' && s.value.k === 'ref' && s.value.v === id) {
            out.push({ from: rec.id, via, to: id, target: this.mintTarget(id) });
          }
        }
      }
    }
    return out;
  }

  private indexEdge(e: EdgeRecord): void {
    this.edges.set(e.id, e);
    let o = this.outEdges.get(e.from);
    if (!o) { o = new Set(); this.outEdges.set(e.from, o); }
    o.add(e.id);
    let i = this.inEdges.get(e.to);
    if (!i) { i = new Set(); this.inEdges.set(e.to, i); }
    i.add(e.id);
  }

  private unindexEdge(e: EdgeRecord): void {
    this.edges.delete(e.id);
    this.outEdges.get(e.from)?.delete(e.id);
    this.inEdges.get(e.to)?.delete(e.id);
  }

  private async mintGrant(
    a: BindingAuthority, mi: ManifestIndex, slot: string, object: ObjectId,
  ): Promise<AnyGrant | Refusal> {
    if (this.closed) return refuse('closed', 'the store is closed');
    const binding = a.writable.get(slot);
    if (!binding) {
      const declared = a.refused.get(slot);
      if (declared) return declared;
      if (a.readOnly.has(slot)) {
        return refuse('read-only-binding',
          `slot "${slot}" is bound read-only, so there is no grant to mutate through`);
      }
      return refuse('unknown-slot', `slot "${slot}" is not bound anywhere in the rendered spec`);
    }

    const keys = (mi.keysByRole.get(binding.role) ?? [])
      .filter((k) => !this.keys.get(k)?.retiredAt);
    if (keys.length === 0) {
      return refuse('unknown-slot', `no property key carries "${binding.role}" in this manifest`);
    }
    if (keys.length > 1) {
      // Visible as a product failure, not just a refusal: the field displays
      // fine — roles project to an array — and refuses to edit, which reads as
      // a bug. The fix is upstream, in how a role acquired two keys.
      return refuse('ambiguous-role',
        `${keys.length} property keys carry "${binding.role}" in this manifest, so a write `
        + 'has no single destination');
    }
    const key = keys[0]!;
    const keyRec = this.keys.get(key)!;
    if (!MUTATION_ROLE_KINDS[binding.mutation].includes(keyRec.kind)) {
      return refuse('kind-mismatch',
        `"${binding.mutation}" cannot write a ${keyRec.kind} property`);
    }
    const rec = this.objects.get(object);
    if (!rec || rec.deletedAt) return refuse('unknown-object', `object "${object}" does not exist`);
    if (a.actor && this.policy && !(await this.policy.canEdit(a.actor, object))) {
      return refuse('not-editable', `actor "${a.actor.id}" may not edit object "${object}"`);
    }
    return {
      [GRANT]: true,
      mutation: binding.mutation,
      object,
      role: binding.role,
      key,
      revision: rec.revision,
    } as AnyGrant;
  }

  /**
   * A grant for one relation mutation on one node slot.
   *
   * Decision 0016's resolution rule, and it is the same one `mintGrant` already
   * performs for a binding: *"runtime must resolve the declared role against
   * the source object's private manifest to exactly one authorized private
   * relation key. Zero matches refuse as missing authority; more than one match
   * refuses as ambiguous rather than choosing by iteration order."*
   *
   * The MUTATION is a parameter rather than a property of the slot, because one
   * slot may declare both and *"runtime grant lookup must support multiple
   * grants per rendered node and select the grant by the person's gesture"*.
   * Attach and detach are two authorities; a slot offering attach alone must
   * not answer a detach.
   */
  private async mintRelationGrant(
    a: BindingAuthority, mi: ManifestIndex, node: string, object: ObjectId,
    mutation: RelationMutation,
  ): Promise<AnyGrant | Refusal> {
    if (this.closed) return refuse('closed', 'the store is closed');
    const slot = a.relationSlots.get(node);
    if (!slot) {
      const declared = a.refused.get(node);
      if (declared) return declared;
      return refuse('unknown-slot',
        `node slot "${node}" declares no relation mutations in the rendered spec`);
    }
    if (!slot.mutations.includes(mutation)) {
      return refuse('undeclared-mutation',
        `node slot "${node}" offers ${slot.mutations.join(' and ')}, not "${mutation}"`);
    }
    const keys = (mi.keysByRole.get(slot.role) ?? [])
      .filter((k) => !this.keys.get(k)?.retiredAt);
    if (keys.length === 0) {
      return refuse('unknown-slot',
        `no property key carries "${slot.role}" in this manifest, so there is no relation `
        + 'set to attach to');
    }
    if (keys.length > 1) {
      return refuse('ambiguous-role',
        `${keys.length} property keys carry "${slot.role}" in this manifest, so a relation `
        + 'mutation has no single destination');
    }
    const key = keys[0]!;
    if (this.keys.get(key)?.kind !== 'ref') {
      return refuse('kind-mismatch', `key for "${slot.role}" is not a ref key`);
    }
    const rec = this.objects.get(object);
    if (!rec || rec.deletedAt) return refuse('unknown-object', `object "${object}" does not exist`);
    if (a.actor && this.policy && !(await this.policy.canEdit(a.actor, object))) {
      return refuse('not-editable', `actor "${a.actor.id}" may not edit object "${object}"`);
    }
    return {
      [GRANT]: true, mutation, object, role: slot.role, key, revision: rec.revision,
    } as AnyGrant;
  }

  /**
   * Mint the opaque handle for one relation target.
   *
   * Called by traversal for every edge it returns — which is the detach half,
   * one handle per rendered occurrence — and by a picker for the attach half.
   * Both go through here, so there is exactly one way a target can enter a
   * mutation and it is never a string a caller typed.
   */
  private mintTarget(object: ObjectId, anchor?: AnchorId): RelationTargetHandle {
    const token = stableHash([this.targetSecret, object, anchor ?? '']);
    if (!this.targets.has(token)) {
      this.targets.set(token, { object, ...opt('anchor', anchor) });
    }
    return { [RELATION_TARGET]: true, token };
  }

  /**
   * Rebrand a token a reader was given back into a usable handle.
   *
   * The detach half of the round trip: projection puts an opaque token on every
   * relation occurrence, the renderer carries it through a DOM attribute, and
   * this turns it back into the branded thing `write` will accept. The brand
   * never leaves this package, which is what stops the string alone from being
   * a write capability.
   *
   * A token this store did not mint refuses, so the trip cannot introduce a
   * target that was never rendered.
   */
  relationTargetFor(a: BindingAuthority, token: string): RelationTargetHandle | Refusal {
    if (!isBindingAuthority(a)) return refuse('not-editable', FORGED);
    if (!this.targets.has(token)) {
      return refuse('unknown-target', 'that handle is not known to this store');
    }
    return { [RELATION_TARGET]: true, token };
  }

  /**
   * Resolve one projected occurrence for READING, without reconstructing it
   * from traversal order. Task 0078.
   *
   * No `BindingAuthority`, unlike `relationTargetFor` beside it, and that is
   * the point rather than an omission: reading and writing are different
   * authorities. The token identifies WHICH occurrence; the scope is still the
   * authority that decides whether its target may be read at all, so an
   * unknown token, a deleted object and one no reachable manifest addresses
   * are the same answer — nothing.
   */
  async occurrenceTarget(scope: Scope, token: string): Promise<ObjectId | undefined> {
    const found = this.targets.get(token);
    if (!found) return undefined;
    const rec = this.objects.get(found.object);
    if (!rec || rec.deletedAt) return undefined;
    const lens = this.lensFor(scope);
    return lens.reachable.some((manifest) => this.addresses(rec, manifest))
      ? found.object
      : undefined;
  }

  relationTarget(a: BindingAuthority, object: ObjectId, anchor?: AnchorId):
  RelationTargetHandle | Refusal {
    // Behind the authority door on purpose. A handle is what makes a write
    // possible, so handing one out to a caller with no authority would be a
    // grant in everything but name.
    if (!isBindingAuthority(a)) return refuse('not-editable', FORGED);
    const rec = this.objects.get(object);
    if (!rec || rec.deletedAt) {
      return refuse('unknown-object', `object "${object}" does not exist`);
    }
    return this.mintTarget(object, anchor);
  }

  private resolveTarget(h: RelationTargetHandle): { object: ObjectId; anchor?: AnchorId } | Refusal {
    if (h?.[RELATION_TARGET] !== true) {
      return refuse('unknown-target', 'that is not a handle this store minted');
    }
    const found = this.targets.get(h.token);
    if (!found) return refuse('unknown-target', 'that handle is not known to this store');
    return found;
  }

  private valueFor(
    i: Instruction, slots: ReadonlyMap<PropertyKeyId, PropertySlot>, keyRec: PropertyKeyRecord,
  ): Value | Clear | Refusal {
    switch (i.mutation) {
      case 'set-value':
        if (i.value === CLEAR) return clearFor(keyRec);
        return valueFitsKind(i.value, keyRec.kind)
          ? i.value
          : refuse('kind-mismatch',
            `a ${kindOf(i.value) ?? 'unknown'} value does not fit a ${keyRec.kind} property`);
      case 'set-enum':
        if (i.value === CLEAR) return clearFor(keyRec);
        return keyRec.kind === 'enum'
          ? { k: 'enum', v: i.value }
          : refuse('kind-mismatch', `set-enum cannot write a ${keyRec.kind} property`);
      case 'set-date':
        if (i.value === CLEAR) return clearFor(keyRec);
        if (keyRec.kind !== 'date') {
          return refuse('kind-mismatch', `set-date cannot write a ${keyRec.kind} property`);
        }
        return /^\d{4}-\d{2}-\d{2}/.test(i.value) && !Number.isNaN(Date.parse(i.value))
          ? { k: 'date', v: i.value }
          : refuse('kind-mismatch', `"${i.value}" is not an ISO-8601 date`);
      case 'toggle': {
        const cur = slots.get(i.grant.key);
        if (cur?.state !== 'set') return { k: 'bool', v: true };
        if (cur.value.k !== 'bool') {
          return refuse('kind-mismatch',
            `toggle needs a two-state value; this slot holds a ${cur.value.k}`);
        }
        return { k: 'bool', v: !cur.value.v };
      }
      // Never reached: `write` splits the relation instructions out before the
      // value loop runs. Answering rather than falling off the end, because a
      // switch that returns undefined here would produce a slot holding it.
      case 'link':
      case 'unlink':
        return refuse('undeclared-mutation',
          `"${i.mutation}" is a relation mutation and writes no value`);
      case 'reorder-within':
        return this.positionAfter(i.grant.key, i.after);
      case 'move-between-groups':
        if (i.after !== null) {
          return refuse('order-not-semantic',
            'move-between-groups writes a grouping value and nothing else; ordering inside '
            + 'the target group needs its own reorder-within binding');
        }
        return keyRec.kind === 'enum'
          ? { k: 'enum', v: i.group }
          : refuse('kind-mismatch', `move-between-groups cannot write a ${keyRec.kind} property`);
    }
  }

  /** Fractional index between the target's position and the next one up, so a
   *  drag rewrites one object rather than renumbering the collection. */
  private positionAfter(key: PropertyKeyId, after: ObjectId | null): Value | Refusal {
    const positions: number[] = [];
    for (const rec of this.objects.values()) {
      if (rec.deletedAt) continue;
      const s = rec.slots.get(key);
      if (s?.state === 'set' && s.value.k === 'number') positions.push(s.value.v);
    }
    positions.sort((a, b) => a - b);
    if (after === null) return { k: 'number', v: positions.length ? positions[0]! - 1 : 0 };

    const anchor = this.objects.get(after)?.slots.get(key);
    if (anchor?.state !== 'set' || anchor.value.k !== 'number') {
      return refuse('order-not-semantic',
        `object "${after}" carries no ordering value, so there is no position to insert after`);
    }
    const at = anchor.value.v;
    const next = positions.find((p) => p > at);
    return { k: 'number', v: next === undefined ? at + 1 : (at + next) / 2 };
  }

  private emptyCommit(via: 'binding' | 'editor', specHash?: string): CommitResult {
    return {
      ok: true,
      journal: {
        id: newJournalId(), at: this.now(), via, ...opt('specHash', specHash),
        touched: [], inverse: [],
      },
      digestRevision: asRevision(this.digestClock),
    };
  }

  /**
   * The one place state changes.
   *
   * Contributions are read BEFORE anything is applied and re-read after, so the
   * digest delta is a difference between two computed values rather than an
   * accumulated guess. That is what makes `rebuildDigest` a parity check rather
   * than a repair job that always finds work.
   */
  private async applyCommit(p: {
    via: 'binding' | 'editor';
    specHash?: string;
    /**
     * The revision the PLAN was made against — decision 0027, required.
     *
     * Not `this.storeRevision` read here, which is the engine's belief AFTER
     * the builder ran: an async builder that reads, awaits and resumes past
     * somebody else's commit would otherwise be accepted against a revision it
     * never saw, its stale reads overwriting the writer that got there first.
     */
    basis: number;
    objects: Map<ObjectId, ObjectRecord | null>;
    keys: Map<PropertyKeyId, PropertyKeyRecord | null>;
    edges: Map<EdgeId, EdgeRecord | null>;
    steps: readonly InverseStep[] | null;
    restore: RestorePatch;
    changedRoles: Set<Role>;
    kind: StoreEvent['kind'];
    /**
     * A manifest to publish in the SAME commit — task `0126`.
     *
     * Already deduped and vocabulary-checked by `manifestRow`, because the
     * caller needs the ref before it can decide anything. Committed as part of
     * this patch and indexed only AFTER the backend takes it: a ref the backend
     * refused must not be readable, which is the half `storeManifest` gets
     * wrong for its own path and cannot be fixed here without changing what a
     * retryable manifest write means.
     */
    manifest?: { ref: ManifestRef; deduped: readonly ManifestEntry[] };
  }): Promise<CommitResult> {
    this.ensureRegistered();
    const affected = new Set<ObjectId>(p.objects.keys());
    for (const [id, edge] of p.edges) {
      const existing = this.edges.get(id);
      if (existing) { affected.add(existing.from); affected.add(existing.to); }
      if (edge) { affected.add(edge.from); affected.add(edge.to); }
    }

    const before = new Map<string, Map<ObjectId, Contribution>>();
    // A traversal scope's membership BEFORE the write, so the objects that
    // joined or left the closure can be named afterwards. An edge write moves
    // members without moving any object's own contribution, so the delta the
    // loop below computes cannot see it.
    const traversalBefore = new Map<string, Set<ObjectId>>();
    for (const [key, counters] of this.digests) {
      if (!this.manifests.has(counters.scope.manifest)) continue;
      const lens = this.lensFor(counters.scope);
      const snapshot = new Map<ObjectId, Contribution>();
      for (const id of affected) {
        const rec = this.objects.get(id);
        if (rec && counters.has(id)) snapshot.set(id, this.contributionOf(rec, lens));
      }
      before.set(key, snapshot);
      if (counters.scope.query?.traverse) traversalBefore.set(key, new Set(counters.members));
    }
    // Subscription is an independent read contract. A traversal scope need not
    // have registered counters, so snapshot every subscribed closure too.
    for (const [key, sub] of this.listeners) {
      if (!sub.scope.query?.traverse || traversalBefore.has(key)
          || !this.manifests.has(sub.scope.manifest)) continue;
      traversalBefore.set(
        key,
        new Set(this.members(sub.scope).map((record) => record.id)),
      );
    }


    for (const [id, rec] of p.keys) {
      if (rec) this.keys.set(id, rec); else this.keys.delete(id);
    }
    for (const [id, edge] of p.edges) {
      const existing = this.edges.get(id);
      if (existing) this.unindexEdge(existing);
      if (edge) this.indexEdge(edge);
    }
    // A key-only or edge-only commit never enters this loop, so it does not pay
    // for a resort on the next read.
    // BEFORE the object mutations, because the adjustment needs both records
    // and `this.objects` is where the old one still lives.
    this.updateFolds(p.objects, p.keys.size > 0, p.objects.size > 0 || p.edges.size > 0);
    // Membership is not adjusted, only dropped — see the field's comment. A
    // key change invalidates it for the same reason it invalidates a fold: a
    // filter reads roles, and a role's keys just changed.
    //
    // EDGES now too. They were absent here because no membership read one; a
    // traversal scope's closure is defined by them, so an edge-only commit
    // leaves a cached membership describing a graph that no longer exists.
    if (p.objects.size > 0 || p.keys.size > 0 || p.edges.size > 0) this.membership.clear();
    for (const [id, rec] of p.objects) {
      if (rec) this.putObject(id, rec); else this.forgetObject(id);
    }

    this.digestClock += 1;
    const clock = this.digestClock;
    for (const [key, counters] of this.digests) {
      if (!this.manifests.has(counters.scope.manifest)) continue;
      if (counters.scope.query?.traverse
          && (p.objects.size > 0 || p.keys.size > 0 || p.edges.size > 0)) {
        // REBUILT, not unregistered. @Codex's ruling refused the `refold`
        // precedent I offered: *"dropping an unregistered fold memo is safe
        // because the next fold read can recreate it. Registered counters are
        // live state with a revision and subscribers, so silently dropping
        // their registration would weaken that contract."*
        //
        // Conservative first cut: rebuild every registered traversal scope.
        // Indexing scopes by relation role is the follow-up optimization; it
        // does not change the edge-driven membership semantic.
        const fresh = this.scan(counters.scope, counters.enumCap);
        fresh.revision = clock;
        this.digests.set(key, fresh);
        continue;
      }
      const lens = this.lensFor(counters.scope);
      const mi = lens.mi;
      let changed = false;
      for (const [id, contribution] of before.get(key) ?? []) {
        counters.remove(id, contribution);
        changed = true;
      }
      for (const id of affected) {
        const rec = this.objects.get(id);
        if (rec && this.inScope(rec, counters.scope, mi)) {
          counters.add(id, this.contributionOf(rec, lens));
          changed = true;
        }
      }
      if (changed) counters.revision = clock;
    }

    // Compute event ids PER traversal subscription. Unioning closure deltas
    // into `affected` leaks one scope's joins and departures to every other
    // subscriber, while a subscribe-only scope has no digest loop to contribute
    // its own delta. Each listener instead gets direct writes that were in its
    // old or new closure, plus that closure's symmetric membership difference.
    const traversalAffected = new Map<string, ReadonlySet<ObjectId>>();
    for (const [key, sub] of this.listeners) {
      if (!sub.scope.query?.traverse || !this.manifests.has(sub.scope.manifest)) continue;
      const old = traversalBefore.get(key) ?? new Set<ObjectId>();
      const fresh = new Set(this.members(sub.scope).map((record) => record.id));
      const relevant = new Set<ObjectId>();
      for (const id of affected) if (old.has(id) || fresh.has(id)) relevant.add(id);
      for (const id of old) if (!fresh.has(id)) relevant.add(id);
      for (const id of fresh) if (!old.has(id)) relevant.add(id);
      traversalAffected.set(key, relevant);
    }


    const record: JournalRecord = {
      id: newJournalId(), at: this.now(), via: p.via, ...opt('specHash', p.specHash),
      touched: [...affected], steps: p.steps, restore: p.restore,
      ...(p.manifest ? { publishedManifest: p.manifest.ref as string } : {}),
    };
    this.journal.set(record.id, record);

    const patch: PersistPatch = {
      objects: [...p.objects.values()].filter((r): r is ObjectRecord => !!r).map(toPersistedObject),
      deletedObjects: [...p.objects].filter(([, r]) => !r).map(([id]) => id as string),
      keys: [...p.keys.values()].filter((r): r is PropertyKeyRecord => !!r).map(toPersistedKey),
      deletedKeys: [...p.keys].filter(([, r]) => !r).map(([id]) => id as string),
      edges: [...p.edges.values()].filter((r): r is EdgeRecord => !!r).map(toPersistedEdge),
      deletedEdges: [...p.edges].filter(([, r]) => !r).map(([id]) => id as string),
      journal: [toPersistedJournal(record)],
      // ONE patch, so one basis — the whole of task `0126`. A value and the
      // manifest entry that makes it reachable are durable together or neither.
      ...(p.manifest
        ? { manifests: [{ ref: p.manifest.ref,
              entries: p.manifest.deduped.map((e) => ({ role: e.role, key: e.key as string })) }] }
        : {}),
    };
    /**
     * CONDITIONAL, and refused rather than merged.
     *
     * Decision 0027. Until this existed the editor door compared nothing: two
     * engines on one database both committed, the later silently won, and a
     * document edited by both would not load again. The comparison is the
     * backend's because an engine's own view is what goes stale.
     *
     * THE BATCH IS IN MEMORY BEFORE THIS RUNS, and the comment that used to
     * sit here claimed the opposite.
     *
     * @Codex, P1 on #164, and the finding is right: applying first means a
     * reader during the await sees a batch that may be refused, and a commit
     * that THROWS leaves it applied with nothing to take it back.
     *
     * The half that can be closed here is the throw, and it is closed below —
     * a backend failure now replaces the engine's state from the backend
     * before the error leaves, so the speculative batch does not survive it.
     *
     * The other half cannot be closed by reordering. Applying after the commit
     * was built, measured, and reverted: it breaks read-after-write inside one
     * engine, and the callers depend on that. On the ordinary URL, with the
     * mutations moved below the await, `e2e/document-canvas.mjs` failed about
     * half of every four runs — the canvas plans each save by reading back what
     * the last one wrote, and the reorder made that read land before the write
     * was visible. Decision 0027 already names the fix: *"every editor-door
     * caller must migrate"*, and until they plan from a snapshot rather than
     * from the engine's live view, ordering alone cannot carry the promise that
     * *"no part of the refused batch is persisted or applied to the engine"*.
     * Task 0103 has the measurement.
     *
     * The refusal path is not exposed, because `stale` replaces every map from
     * the backend before it returns.
     */
    let outcome: CommitOutcome;
    try {
      outcome = await this.persistence.commit(patch, {
        expect: p.basis, next: p.basis + 1,
      });
    } catch (e) {
      // The batch is already in the maps and the backend never took it. Put the
      // engine back to what the backend actually holds before the error leaves,
      // or every later read is planned against a write that does not exist.
      await this.hydrate({}, true).catch(() => {});
      throw e;
    }
    if (!outcome.ok) return this.stale(outcome.current);
    this.storeRevision = p.basis + 1;
    // AFTER the backend accepted it, and outside the refusal path entirely.
    // A hint that a commit landed which did not land would send every other tab
    // to reload state that never existed.
    //
    // And it CANNOT change the outcome of a commit the backend has taken.
    // @Codex, P1 on #166: *"the backend has already accepted the patch, but
    // this method rejects before emitting store events or returning
    // `{ ok: true }`. The caller therefore sees a failed write that actually
    // persisted and may retry a non-idempotent edit against altered state."*
    //
    // Correct, and reachable: `announceOn` posts to a `BroadcastChannel`, and
    // posting on a closed one throws `InvalidStateError`. Swallowed rather than
    // reported because there is no error channel at this layer and the
    // notification is best-effort by nature — nothing is listening when a
    // person has one tab open. What must not be best-effort is the answer this
    // method returns about durable state.
    this.announceCommitted();

    // AFTER the backend accepted it, for the reason the block above gives about
    // announcements: a ref readable in this engine but refused by the backend
    // is a role that resolves here and nowhere else.
    if (p.manifest) {
      this.manifests.set(p.manifest.ref, indexManifest(p.manifest.ref, [...p.manifest.deduped]));
      this.ensureRegistered();
    }

    this.emit(affected, p.changedRoles, clock, p.kind, traversalAffected);
    return {
      ok: true,
      journal: publicJournal(record),
      digestRevision: asRevision(clock),
      ...(p.manifest ? { manifest: p.manifest.ref } : {}),
    };
  }

  /**
   * A refusal that leaves the engine able to try again.
   *
   * Decision 0027 requires the **authoritative replacement refresh to happen
   * here, inside the refusal path**, before `stale` is returned — and NOT in
   * `snapshot()`, which would make every keystroke a full database load.
   *
   * Measured before it existed: a second tab never learns what the first wrote,
   * so re-reading and retrying returns the same stale facts for ever. That is
   * the difference between a refusal meaning "try again" and meaning "you are
   * done" — task 0103.
   *
   * REPLACEMENT, not merge — `hydrate`'s second argument, whose comment holds
   * why. And a refresh that fails is reported as a backend failure rather than
   * dressed up as a recoverable `stale`, because a caller told to retry against
   * state that never arrived would loop. It also leaves the engine untouched:
   * this used to clear the maps and then await the load, so a backend failure
   * stranded the engine permanently empty.
   */
  private async stale(current: number): Promise<Refusal> {
    try {
      await this.hydrate({}, true);
    } catch (e) {
      return refuse('backend',
        `the commit was refused as stale at revision ${current}, and reloading `
        + `the store failed: ${e instanceof Error ? e.message : String(e)}`);
    }
    return refuse('stale',
      `this write was planned against an older revision than ${current}; the store `
      + 'has moved and the engine has been refreshed, so plan it again');
  }

  private emit(
    changed: ReadonlySet<ObjectId>, roles: ReadonlySet<Role>, clock: number,
    kind: StoreEvent['kind'],
    traversalAffected: ReadonlyMap<string, ReadonlySet<ObjectId>>,
  ): void {
    for (const [key, sub] of this.listeners) {
      const mi = this.manifests.get(sub.scope.manifest);
      if (!mi) continue;
      const traversal = sub.scope.query?.traverse !== undefined;
      // A traversal scope already has its own before/after membership answer.
      // Other scopes retain the ordinary value filtering contract.
      const relevant = traversal
        ? [...(traversalAffected.get(key) ?? [])]
        : kind === 'values'
          ? [...changed].filter((id) => {
            const rec = this.objects.get(id);
            return !!rec && this.inScope(rec, sub.scope, mi);
          })
          : [...changed];
      if (!relevant.length) continue;
      const event: StoreEvent = {
        scopeKey: key,
        changed: relevant,
        roles: [...roles],
        digestRevision: asRevision(this.digests.get(key)?.revision ?? clock),
        kind,
      };
      for (const listener of sub.listeners) listener(event);
    }
  }
}

/* ──────────────────────────────────────────────────────────────── helpers */

type EditOp =
  | { readonly t: 'create'; readonly id: ObjectId; readonly typeHint?: string;
      readonly properties: readonly (readonly [PropertyKeyId, Value])[] }
  | { readonly t: 'define'; readonly rec: PropertyKeyRecord }
  | { readonly t: 'set'; readonly o: ObjectId; readonly key: PropertyKeyId; readonly v: Value }
  | { readonly t: 'clear'; readonly o: ObjectId; readonly key: PropertyKeyId }
  | { readonly t: 'drop'; readonly o: ObjectId; readonly key: PropertyKeyId }
  | { readonly t: 'link'; readonly edge: EdgeRecord; readonly keyed: boolean }
  | { readonly t: 'unlink'; readonly edge: EdgeId }
  | { readonly t: 'retire'; readonly o: ObjectId }
  | { readonly t: 'bindRole'; readonly key: PropertyKeyId; readonly role: Role }
  /**
   * A manifest entry, folded into the SAME commit as the values it makes
   * reachable — task `0126`.
   *
   * Not a mutation of an object, which is why it carries no `o`: it changes
   * what a role resolves to, and the whole point of it being an op rather than
   * a separate call is that it lands with one basis alongside the
   * `defineProperty` that minted its key.
   */
  | { readonly t: 'manifest'; readonly role: Role; readonly key: PropertyKeyId };

type FilterNode = NonNullable<TQuery['filter']>;

interface FoundBinding { readonly slot: string; readonly binding: TBinding; readonly sorted: boolean }

/** Every binding in the tree that will actually render, with whether it sits
 *  inside a sorted collection — the one piece of context a reorder needs and
 *  the binding itself cannot carry. */
function collectBindings(root: TNode): FoundBinding[] {
  const out: FoundBinding[] = [];
  const walk = (n: TNode, sorted: boolean): void => {
    switch (n.kind) {
      case 'region':
        for (const c of n.children) walk(c, sorted);
        break;
      case 'collection': {
        const inner = sorted || (n.query?.sort?.length ?? 0) > 0;
        for (const it of n.items) walk(it.spec, inner);
        break;
      }
      case 'card':
        for (const b of n.bindings) out.push({ slot: b.slot, binding: b, sorted });
        for (const c of n.children ?? []) walk(c, sorted);
        break;
      case 'field':
      case 'media':
        out.push({ slot: n.binding.slot, binding: n.binding, sorted });
        break;
      case 'capability':
        for (const binding of Object.values(n.channels ?? {})) {
          out.push({ slot: binding.slot, binding, sorted });
        }
        for (const kids of Object.values(n.slots ?? {})) for (const c of kids) walk(c, sorted);
        break;
      default:
        // A node slot carries no `Binding`. It declares `mutations`, which
        // `collectRelationSlots` reads — two different shapes of authority for
        // two different things, which is decision 0016's point.
        //
        // Its CHILDREN are deliberately not walked either, and that is a
        // limitation rather than a fact about node slots. A hosted spec is a
        // second complete spec, and two specs in one tree collide on binding
        // SLOT NAMES exactly as they collided on node ids — `namespaced()` in
        // the demo renames ids and nothing renames slots, so walking them would
        // hand the main lens `ambiguous-role` refusals for slots it owns.
        //
        // So a hosted spec is read-only, by omission here rather than by
        // anything saying so. `test/hosted-bindings.test.ts` says it out loud,
        // so it cannot change quietly.
        break;
    }
  };
  walk(root, false);
  return out;
}

/**
 * Node slots that declared relation mutations, by node id.
 *
 * Keyed by the node's OWN id rather than by a slot name: a node slot has no
 * slot, which is the structural difference decision 0016 turns on — a
 * `Binding.mutation` holds one mutation, and attach and detach are two.
 */
function collectRelationSlots(root: TNode): { id: string; via: Role; mutations: readonly RelationMutation[] }[] {
  const out: { id: string; via: Role; mutations: readonly RelationMutation[] }[] = [];
  const walk = (n: TNode): void => {
    switch (n.kind) {
      case 'region':
        for (const c of n.children) walk(c);
        break;
      case 'collection':
        for (const it of n.items) walk(it.spec);
        break;
      case 'card':
        for (const c of n.children ?? []) walk(c);
        break;
      case 'capability':
        for (const kids of Object.values(n.slots ?? {})) for (const c of kids) walk(c);
        break;
      case 'node-slot':
        if (n.mutations?.length) out.push({ id: n.id, via: n.via, mutations: n.mutations });
        for (const c of n.children ?? []) walk(c);
        break;
      default:
        break;
    }
  };
  walk(root);
  return out;
}

function indexManifest(ref: ManifestRef, entries: readonly ManifestEntry[]): ManifestIndex {
  const keysByRole = new Map<Role, PropertyKeyId[]>();
  const roleByKey = new Map<PropertyKeyId, Role>();
  for (const e of entries) {
    let list = keysByRole.get(e.role);
    if (!list) { list = []; keysByRole.set(e.role, list); }
    if (!list.includes(e.key)) list.push(e.key);
    roleByKey.set(e.key, e.role);
  }
  return { ref, entries, keysByRole, roleByKey };
}

function paginate<T>(all: readonly T[], page: Page): { items: T[]; cursor?: string } {
  if ('offset' in page && page.offset !== undefined && page.cursor !== undefined) {
    throw new RangeError('page may name an offset or a cursor, never both');
  }
  const requested = 'offset' in page && page.offset !== undefined
    ? page.offset
    : page.cursor ? Number.parseInt(page.cursor, 36) : 0;
  if (!Number.isSafeInteger(requested) || requested < 0) {
    throw new RangeError('page offset must be a non-negative safe integer');
  }
  if (!Number.isSafeInteger(page.limit) || page.limit < 0) {
    throw new RangeError('page limit must be a non-negative safe integer');
  }
  const items = all.slice(requested, requested + page.limit);
  const next = requested + items.length;
  return { items, ...(next < all.length ? { cursor: next.toString(36) } : {}) };
}

/**
 * Clearing is the same act whatever the slot holds, so it is checked against
 * the KIND rather than against a payload — nothing fits every kind, and asking
 * each arm whether nothing fits its own would be asking the wrong question.
 *
 * Media and refs are excluded: a blob and an edge are released through their
 * own doors, and a slot emptied here would strand what it pointed at.
 */
function clearFor(keyRec: PropertyKeyRecord): Clear | Refusal {
  return keyRec.kind === 'media' || keyRec.kind === 'ref'
    ? refuse('kind-mismatch',
      `a ${keyRec.kind} property is released through its own door, not cleared as a value`)
    : CLEAR;
}

const SCALAR_MUTATIONS = ['set-value', 'set-enum', 'set-date', 'toggle'] as const;
type ScalarMutation = (typeof SCALAR_MUTATIONS)[number];
function isScalarMutation(m: Instruction['mutation']): m is ScalarMutation {
  return (SCALAR_MUTATIONS as readonly string[]).includes(m);
}

/** Rebuild the public inverse from the recorded steps. Grants are minted here
 *  rather than persisted: a `Grant` carries a symbol brand, and no serializer
 *  preserves one — a stored grant would deserialize into something that only
 *  looked like a grant, which is precisely the forgery the brand prevents. */
function publicJournal(rec: JournalRecord): JournalEntry {
  const inverse: readonly Instruction[] | 'editor-inverse' = rec.steps
    ? rec.steps.map((s) => {
      const grant = {
        [GRANT]: true, mutation: s.mutation, object: s.object as ObjectId, role: s.role,
        key: s.key as PropertyKeyId, revision: asRevision(s.revision),
      };
      switch (s.mutation) {
        case 'set-value':
          return { mutation: 'set-value', grant: grant as Grant<'set-value'>, value: s.before };
        case 'set-enum':
          return { mutation: 'set-enum', grant: grant as Grant<'set-enum'>,
            value: String(rawOf(s.before)) };
        case 'set-date':
          return { mutation: 'set-date', grant: grant as Grant<'set-date'>,
            value: String(rawOf(s.before)) };
        case 'toggle':
          return { mutation: 'toggle', grant: grant as Grant<'toggle'> };
      }
    })
    : 'editor-inverse';
  return {
    id: rec.id, at: rec.at, via: rec.via, ...opt('specHash', rec.specHash),
    touched: rec.touched, inverse,
  };
}

/** Copies, not the live maps. A digest handed to a caller that then mutated it
 *  would corrupt the counters silently, and the corruption would look exactly
 *  like the incremental-drift bug `rebuildDigest` exists to catch. */
function toDigest(c: Counters, key: string): ShapeDigest {
  const distinctByKey = new Map<PropertyKeyId, number>();
  for (const [k, sketch] of c.enumValues) {
    if (sketch.counts.size) distinctByKey.set(k, sketch.counts.size);
  }
  const distinctPairs = new Map<string, number>();
  for (const [k, sketch] of c.enumPairs) {
    if (sketch.counts.size) distinctPairs.set(k, sketch.counts.size);
  }
  const orderedDistinctByKey = new Map<PropertyKeyId, number>();
  for (const [k, sketch] of c.orderedValues) {
    if (sketch.counts.size) orderedDistinctByKey.set(k, sketch.counts.size);
  }
  return {
    scopeKey: key,
    revision: asRevision(c.revision),
    total: c.total,
    populatedByKey: new Map(c.populatedByKey),
    objectsByRole: new Map(c.objectsByRole),
    sourceWithoutDerivative: new Map(c.sourceWithoutDerivative),
    distinctByKey,
    distinctPairs,
    orderedDistinctByKey,
    relationTotal: c.relationTotal,
    relationObjects: c.relationObjects,
    crossDocTotal: c.crossDocTotal,
  };
}

function testPredicate(values: readonly Value[], op: Operator, arg: Value | undefined): boolean {
  if (op === 'is-empty') return values.length === 0;
  if (op === 'is-not-empty') return values.length > 0;
  if (arg === undefined) return false;
  const target = rawOf(arg);
  const raws = values.map(rawOf);

  switch (op) {
    case 'equals':       return raws.some((v) => sameRaw(v, target));
    case 'not-equals':   return !raws.some((v) => sameRaw(v, target));
    case 'in':           return Array.isArray(target)
      ? raws.some((v) => target.some((t) => sameRaw(v, t)))
      : raws.some((v) => sameRaw(v, target));
    case 'contains':     return raws.some((v) => String(v).includes(String(target)));
    case 'before':       return raws.some((v) => compareRaw(v, target) < 0);
    case 'after':        return raws.some((v) => compareRaw(v, target) > 0);
    case 'greater-than': return raws.some((v) => compareRaw(v, target) > 0);
    case 'less-than':    return raws.some((v) => compareRaw(v, target) < 0);
    case 'between': {
      if (!Array.isArray(target) || target.length < 2) return false;
      const [lo, hi] = target;
      return raws.some((v) => compareRaw(v, lo) >= 0 && compareRaw(v, hi) <= 0);
    }
    default:             return false;
  }
}

const sameRaw = (a: unknown, b: unknown): boolean =>
  a === b || (a !== null && b !== null && typeof a === 'object' && typeof b === 'object'
    && JSON.stringify(a) === JSON.stringify(b));

/** Undefined sorts last in both directions: a missing value is not "smaller",
 *  it is a hole, and a hole floating to the top of a sorted view reads as data
 *  loss. */
function compareRaw(a: unknown, b: unknown): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' || typeof b === 'boolean') return Number(a) - Number(b);
  const as = String(a);
  const bs = String(b);
  const ad = Date.parse(as);
  const bd = Date.parse(bs);
  if (!Number.isNaN(ad) && !Number.isNaN(bd) && /^\d{4}-\d{2}-\d{2}/.test(as)) return ad - bd;
  return as.localeCompare(bs);
}

const toPersistedObject = (r: ObjectRecord): PersistedObject => ({
  id: r.id, revision: r.revision, ...opt('typeHint', r.typeHint),
  slots: [...r.slots].map(([k, s]) => [k as string, s] as const),
  ...opt('deletedAt', r.deletedAt),
});

const fromPersistedObject = (p: PersistedObject): ObjectRecord => ({
  id: p.id as ObjectId, revision: asRevision(p.revision), ...opt('typeHint', p.typeHint),
  slots: new Map(p.slots.map(([k, s]) => [k as PropertyKeyId, s])),
  ...opt('deletedAt', p.deletedAt),
});

const toPersistedKey = (r: PropertyKeyRecord): PersistedKey => ({
  id: r.id, label: r.label, kind: r.kind, ...opt('roleHint', r.roleHint),
  createdAt: r.createdAt, ...opt('retiredAt', r.retiredAt),
});

const fromPersistedKey = (p: PersistedKey): PropertyKeyRecord => ({
  id: p.id as PropertyKeyId, label: p.label, kind: p.kind, ...opt('roleHint', p.roleHint),
  createdAt: p.createdAt, ...opt('retiredAt', p.retiredAt),
});

/**
 * Does this edge belong to the relation a reader is asking for?
 *
 * A KEYED edge answers by key, which is the whole change: a manifest that does
 * not name the key does not see the edge, exactly as it does not see a slot it
 * cannot address. That is the manifest boundary reaching edges for the first
 * time — before this, `e.via === role` meant any manifest naming the public
 * word could read any tenant's edge under it.
 *
 * A KEYLESS edge — anything written before edges had a key — answers by role,
 * unchanged. Reading it is not optional: a stored relation that stopped
 * projecting on upgrade would be data loss dressed as a privacy fix.
 */
const edgeIsUnder = (
  e: EdgeRecord, role: Role, keys: readonly PropertyKeyId[],
): boolean => (e.key !== undefined ? keys.includes(e.key) : e.via === role);

const toPersistedEdge = (e: EdgeRecord): PersistedEdge => ({
  id: e.id, from: e.from, via: e.via, to: e.to,
  ...opt('key', e.key as string | undefined),
  ...opt('anchor', e.anchor as string | undefined),
  at: e.at,
});

/** `key` is absent in anything written before edges had one. The edge still
 *  reads — `via` is what projection falls back to — and is simply not mutable,
 *  which is a smaller loss than dropping a seeded relation on upgrade. */
const fromPersistedEdge = (p: PersistedEdge): EdgeRecord => ({
  id: p.id as EdgeId, from: p.from as ObjectId, via: p.via, to: p.to as ObjectId,
  ...opt('key', p.key as PropertyKeyId | undefined),
  ...opt('anchor', p.anchor as AnchorId | undefined), at: p.at,
});

const toPersistedJournal = (r: JournalRecord): PersistedJournal => ({
  id: r.id, at: r.at, via: r.via, ...opt('specHash', r.specHash),
  touched: r.touched as readonly string[], steps: r.steps, restore: r.restore,
  ...opt('publishedManifest', r.publishedManifest),
});
